import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Settings, Shuffle, Play, Pause, Edit2, ExternalLink, Trash2, Sun, Moon, X, Save, Upload, Maximize2, Minimize2, LogOut } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged, deleteUser, reauthenticateWithPopup } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, getDocs, deleteDoc, getDoc, query, where, limit, serverTimestamp } from 'firebase/firestore';
import termsPdf from './terms-of-service.pdf';
import INITIAL_CARDS, { INITIAL_DECKS } from './initialCards';
import { TIERS, DEFAULT_PLAN, getTier, cardsRemaining } from './tiers';
import { createCardWithCounter, deleteCardWithCounter, wipeCardsAndCounter } from './cardCounter';
import { parsePublicHttpUrl, safeHref } from './urlSafety';
import CardSkeletons from './CardSkeletons';
import DeckSelect from './DeckSelect';
import { tagKey, dedupeTags, buildCanonicalMap, resolveTag, mergeCardTags } from './tagUtils';

// Firebase configuration — values injected from environment at build time.
// Local dev: set them in .env.local (gitignored). Netlify: set them in Site settings → Environment.
// See .env.example for the full list of required variables.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('Firebase config missing — set REACT_APP_FIREBASE_* variables in .env.local (dev) or the Netlify environment (build). See .env.example.');
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
getAnalytics(app); // Initialize Firebase Analytics (side-effect only)
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

const TAG_COLORS = {
  'AI': 'bg-purple-500', 'automation': 'bg-blue-500', 'app builder': 'bg-green-500',
  'web builder': 'bg-teal-500', 'design app': 'bg-pink-500', 'image generator': 'bg-orange-500',
  'image restore': 'bg-yellow-500', 'audio editor': 'bg-red-500', 'note taking': 'bg-indigo-500',
  'tutorial': 'bg-cyan-500', 'resume builder': 'bg-lime-500', 'documentation': 'bg-amber-500',
  'template': 'bg-violet-500', 'reference': 'bg-fuchsia-500', 'self-host': 'bg-rose-500',
  'api': 'bg-emerald-500', 'daily use': 'bg-sky-500', 'social media': 'bg-blue-400',
  'music': 'bg-green-400', 'encyclopedia': 'bg-gray-500', 'video': 'bg-red-400',
  'education': 'bg-teal-400', 'online school': 'bg-blue-300', 'recipe': 'bg-orange-400',
  'gaming': 'bg-purple-400', 'wow': 'bg-red-600', 'sales': 'bg-green-600', 
  'business': 'bg-slate-600', 'selling': 'bg-emerald-600', 'ui': 'bg-indigo-600',
  'ui components': 'bg-purple-600', 'journal': 'bg-amber-600', 'analytics': 'bg-cyan-600',
  'voice ai': 'bg-violet-600', 'programs': 'bg-blue-700', 'software': 'bg-slate-700',
  'web security': 'bg-red-700', 'tips': 'bg-yellow-600', 'food': 'bg-orange-600',
  'hacks': 'bg-lime-600', 'no code': 'bg-teal-600', 'image generation': 'bg-pink-600',
  'image to image': 'bg-fuchsia-600', 'text to image': 'bg-rose-600', 'web storage': 'bg-sky-600',
  'learning': 'bg-green-500', 'database': 'bg-indigo-700'
};

const TAG_ICON_MAP = {
  'AI': '🤖', 'automation': '⚙️', 'app builder': '🏗️', 'web builder': '🌐',
  'design app': '🎨', 'image generator': '🖼️', 'image restore': '✨',
  'audio editor': '🎧', 'note taking': '📝', 'tutorial': '📖',
  'resume builder': '📄', 'documentation': '📚', 'template': '📋',
  'reference': '🔖', 'self-host': '🏠', 'api': '🔌', 'daily use': '⭐',
  'social media': '👥', 'music': '🎵', 'encyclopedia': '📚', 'video': '🎥',
  'education': '🎓', 'online school': '💻', 'recipe': '🍳', 'gaming': '🎮', 'wow': '⚔️',
  'sales': '💰', 'business': '💼', 'selling': '🤝', 'ui': '🎨', 'ui components': '🧩',
  'journal': '📔', 'analytics': '📊', 'voice ai': '🎙️', 'programs': '💾',
  'software': '⚙️', 'web security': '🔒', 'tips': '💡', 'food': '🍔',
  'hacks': '🔧', 'no code': '🚫', 'image generation': '🎨', 'image to image': '🔄',
  'text to image': '✍️', 'web storage': '☁️', 'learning': '📚', 'database': '🗄️'
};

// "Quiet Archive" restyle (v1.3.0) — tag chips are tint background + hue-matched text
// instead of solid saturated pills. Hue derives from TAG_COLORS so the two stay in sync;
// the generated class matrix is safelisted in tailwind.config.js.
// Case-insensitive lookups — "Github" and "github" must look and behave the same.
const byTagKey = (table) => Object.fromEntries(Object.entries(table).map(([k, v]) => [tagKey(k), v]));
const TAG_COLORS_BY_KEY = byTagKey(TAG_COLORS);
const TAG_ICONS_BY_KEY = byTagKey(TAG_ICON_MAP);
const BUILT_IN_TAGS = Object.keys(TAG_COLORS);

const tagChipClass = (tag) => {
  const bg = TAG_COLORS_BY_KEY[tagKey(tag)] || 'bg-gray-500';
  const hue = (bg.match(/^bg-([a-z]+)-/) || [])[1] || 'gray';
  return `${bg} bg-opacity-10 dark:bg-opacity-20 text-${hue}-700 dark:text-${hue}-300 font-medium`;
};

// Status is always dot + label, never a loud badge (Restyle Spec §1 semantic rules)
const STATUS_DOT = { active: 'bg-accent', draft: 'bg-warn', archive: 'bg-ink3' };

const DEFAULT_GEMINI_KEY = 'YOUR_GEMINI_API_KEY_HERE';
const APP_VERSION = '1.3.5';

// Card grid paging — show a first page, then extend on demand (keeps big vaults fast).
const CARDS_INITIAL_PAGE = 20;
const CARDS_PAGE_STEP = 10;
// Press-and-hold duration (ms) required to delete a card from the grid.
const HOLD_DELETE_MS = 3000;
// Longer hold for irreversible account deletion — user asked for 8s to reduce accidental taps.
const HOLD_DELETE_ACCOUNT_MS = 8000;
// Max characters shown for a card's URL before it's ellipsised.
const CARD_URL_MAX_CHARS = 25;
// Max existing-tag matches offered while typing a new tag.
const TAG_AUTOCOMPLETE_LIMIT = 6;
// Must mirror firestore.rules — a card over these limits is rejected as "permission-denied".
// The per-user card cap is tier-based (src/tiers.js, v1.3.4) — no flat vault limit any more.
const CARD_TITLE_MAX = 200;
const CARD_URL_MAX = 250;
const CARD_TAGS_MAX = 15;
// Client-side error log — capped ring buffer in localStorage (see logAppError).
const ERROR_LOG_KEY = 'richvault_error_log';
const ERROR_LOG_MAX = 50;
// In-progress Add Card form, autosaved so it survives closing the modal or the tab.
const DRAFT_CARD_KEY = 'richvault_draft_card';
// Skip re-fetching cards/decks/settings from Firestore on login if this user was
// already synced within this window (AD006) — cuts repeat-open/reload Firestore reads.
const FIRESTORE_SYNC_STALE_MS = 5 * 60 * 1000;

// Onboarding Modal Component
function OnboardingModal({ isOpen, onComplete, onSkip, theme }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const cardBg = 'bg-surface border border-line';
  const inputBg = 'bg-inset border border-line';
  
  const slides = [
    {
      title: "Your personal vault for the internet",
      subtitle: "Save it. Tag it. Find it instantly.",
      content: "Save useful websites, tools, apps, and images as cards with descriptions and tags, so you can find them when you need them.",
      image: "onboarding-slide-1.png",
      imageAlt: "Saved learning websites in the RichVault card grid"
    },
    {
      title: "Made for how you actually browse",
      subtitle: "Personal saves or work resources, it fits both",
      content: "Freelancer saving client resources and tools? Student or designer building a library of useful resources? Developer bookmarking docs and repos? RichVault keeps it all organized so you stop losing things in your tabs.",
      image: "onboarding-slide-2.png",
      imageAlt: "Client work, learning, and daily use examples"
    },
    {
      title: "Start a card with a URL",
      subtitle: "Give a useful site a place in your vault",
      content: "Select Add Card, enter a title, and paste the website address. This example saves Class Central's free certificate collection.",
      image: "onboarding-slide-3.png",
      imageAlt: "Add Card form with Class Central title and URL"
    },
    {
      title: "Generate a helpful description",
      subtitle: "Add context you can search later",
      content: "Select Generate to fill a short description from available site information and see tag suggestions. You can edit the description yourself; optional AI support uses your own key.",
      image: "onboarding-slide-4.png",
      imageAlt: "Generate button beside a description and suggested tags"
    },
    {
      title: "Choose your smart tags",
      subtitle: "Keep the labels that help you find it",
      content: "Click suggested tags to add the ones you want. Reuse an existing tag or type your own. Suggestions are saved only after you choose them and save the card.",
      image: "onboarding-slide-5.png",
      imageAlt: "Selected learning and certificates tags in the Add Card form"
    },
    {
      title: "Save the finished card",
      subtitle: "Your link and context stay together",
      content: "Select Save Card. RichVault keeps the link, description, and chosen tags together so you can return to this resource later.",
      image: "onboarding-slide-6.png",
      imageAlt: "Save Card button below the completed Class Central card"
    },
    {
      title: "Create a Deck for a topic",
      subtitle: "Name it and pick a colour",
      content: "Select Create Deck, enter a name such as Learning Sites, choose a colour, and select Create Deck. Use Decks to keep a project or topic together.",
      image: "onboarding-slide-7.png",
      imageAlt: "Create Personal Deck dialog with Learning Sites name and colour choices"
    },
    {
      title: "Organize with Decks",
      subtitle: "Group related cards in one collection",
      content: "Assign cards to a Deck while saving or editing them. Select a Deck to focus on that collection, such as your saved learning sites.",
      image: "onboarding-slide-8.png",
      imageAlt: "Learning Sites deck selected above saved cards"
    },
    {
      title: "Find and open a saved card",
      subtitle: "Search what you remember",
      content: "Search by title, description, web address, or tag. Type certificates to find the Class Central card, then select Open to visit the site.",
      image: "onboarding-slide-9.png",
      imageAlt: "Search for certificates showing the saved Class Central card"
    },
    {
      title: "Share a Deck with anyone",
      subtitle: "One code. Instant collection.",
      content: "Publish a Deck to create a share code. Someone else can enter that code in RichVault to preview and import the collection.",
      image: "onboarding-slide-10.png",
      imageAlt: "Publishing the Learning Sites deck to get a share code"
    },
    {
      title: "Rediscover what you saved",
      subtitle: "The shuffle that surfaces forgotten gems",
      content: "Hit the Shuffle button to enter Discovery mode, RichVault cycles through your cards automatically. Great for inspiration, revisiting saved tools, or just browsing your vault without searching.",
      image: "onboarding-slide-11.png",
      imageAlt: "Discovery mode showing saved learning cards and Shuffle"
    },
    {
      title: "A few power moves",
      subtitle: "Get more out of RichVault",
      content: "Use search filters to narrow cards by status, tag, or Deck, or hide a tag you don't want to see. Sign in with Google when you want your vault to sync across devices.",
      image: "onboarding-slide-12.png",
      imageAlt: "Status, tag, and Deck filters above saved cards"
    },
    {
      title: "You're all set!",
      subtitle: "Your vault is ready — let's fill it",
      content: "Start with one useful link you want to revisit. Add a title, choose tags, and save it. Sign in with Google if you want your vault to sync across devices.\n\nWelcome to RichVault.",
      image: "onboarding-slide-13.png",
      imageAlt: "RichVault cards ready to explore"
    }
  ];

  const totalSlides = slides.length;
  const isLastSlide = currentSlide === totalSlides - 1;

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleComplete = () => {
    onComplete();
    setCurrentSlide(0);
  };

  const handleSkip = () => {
    onSkip();
    setCurrentSlide(0);
  };

  if (!isOpen) return null;

  return (
    // Outer overlay uses dvh (dynamic viewport) so mobile browsers with a
    // collapsing URL bar (iPhone 16 Pro Safari, Chrome iOS) don't clip the
    // footer under the chrome. Fallback: 100vh via inline style for browsers
    // that don't support dvh yet.
    <div
      className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-2 sm:p-4 z-50"
      style={{ minHeight: '100vh' }}>
      <div
        className={`${cardBg} rounded-modal shadow-modal max-w-3xl w-full flex flex-col overflow-hidden`}
        style={{ maxHeight: 'min(90dvh, 100vh - 1rem)' }}>
        {/* Header — flex-shrink-0 keeps it visible even when content scrolls */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-line flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold truncate">Getting Started</h2>
              <p className="text-xs sm:text-sm opacity-70 truncate">Quick tour of RichVault</p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="px-3 sm:px-4 py-2 text-ink2 hover:text-ink hover:bg-inset rounded-btn transition-colors text-sm font-medium flex-shrink-0">
            Skip Tour
          </button>
        </div>

        {/* Slide Content — flex-1 + overflow-y-auto makes the middle region
            the ONLY thing that scrolls, guaranteeing header + footer visible */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="transition-all duration-300 ease-in-out">
            {/* Slide Title */}
            <h3 className="text-xl sm:text-3xl font-semibold mb-2">{slides[currentSlide].title}</h3>
            <p className="text-base sm:text-lg opacity-80 mb-4 sm:mb-6">{slides[currentSlide].subtitle}</p>

            {/* Image/GIF — only rendered when the slide defines an image field */}
            {slides[currentSlide].image && (
              <div className={`${inputBg} rounded-card mb-4 sm:mb-6 overflow-hidden`} style={{ aspectRatio: '16/9', width: '100%' }}>
                <img
                  src={`/onboarding/${slides[currentSlide].image}`}
                  alt={slides[currentSlide].imageAlt}
                  className="w-full h-full object-cover rounded-card"
                  onError={e => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="text-center p-8 flex-col items-center justify-center hidden w-full h-full text-sm text-ink3">
                  {slides[currentSlide].imageAlt}
                </div>
              </div>
            )}

            {/* Slide Content Text */}
            <div className="text-sm sm:text-base leading-relaxed whitespace-pre-line opacity-90">
              {slides[currentSlide].content}
            </div>
          </div>
        </div>

        {/* Footer — flex-shrink-0 pins Next / Skip so buttons are ALWAYS reachable */}
        <div className="p-4 sm:p-6 border-t border-line flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
          {/* Progress Indicators */}
          <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'w-8 bg-accent'
                    : 'w-2 bg-line hover:bg-line2'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Slide Counter */}
          <p className="text-center text-xs sm:text-sm opacity-50 mb-3 sm:mb-4">
            {currentSlide + 1} of {totalSlides}
          </p>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <button
              onClick={prevSlide}
              disabled={currentSlide === 0}
              className={`px-4 sm:px-6 py-3 rounded-btn font-medium transition-colors text-sm sm:text-base ${
                currentSlide === 0
                  ? 'bg-inset border border-line text-ink3 cursor-not-allowed'
                  : 'bg-inset border border-line hover:bg-inset2 text-ink'
              }`}>
              Previous
            </button>

            {isLastSlide ? (
              <button
                onClick={handleComplete}
                className="flex-1 px-4 sm:px-6 py-3 bg-accent hover:bg-accent2 text-white rounded-btn font-medium transition-colors text-sm sm:text-base">
                ✓ Start Using RichVault
              </button>
            ) : (
              <button
                onClick={nextSlide}
                className="flex-1 px-4 sm:px-6 py-3 bg-accent hover:bg-accent2 text-white rounded-btn font-medium transition-colors text-sm sm:text-base">
                Next →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RichVaultSmartTags() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [user, setUser] = useState(null);
  // Plan key from users/{uid}/entitlements/tier (read-only for clients). Guests and
  // accounts with no entitlement doc are on the free Low tier.
  const [userPlan, setUserPlan] = useState(DEFAULT_PLAN);
  const tier = getTier(userPlan);
  const [loading, setLoading] = useState(true);
  const [cardsSyncing, setCardsSyncing] = useState(false); // signed-in Firestore load in flight
  const showSkeleton = loading || cardsSyncing;
  const [cards, setCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTagFilter, setSelectedTagFilter] = useState('all');
  // "NOT" filter: cards carrying any of these tags are hidden from results.
  const [excludedTags, setExcludedTags] = useState([]);
  const [selectedDeckFilter, setSelectedDeckFilter] = useState('all');
  const [showDeckView, setShowDeckView] = useState(false);
  const [isDiscoveryMinimized, setIsDiscoveryMinimized] = useState(true);
  const [showSearchFilters, setShowSearchFilters] = useState(false);
  const [isRecentlyAddedMinimized, setIsRecentlyAddedMinimized] = useState(false);
  const [recentFilterStatus, setRecentFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPersonalDeckModal, setShowPersonalDeckModal] = useState(false);
  const [showDeleteDeckModal, setShowDeleteDeckModal] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);
  const [showShareDeckModal, setShowShareDeckModal] = useState(false);
  const [showImportDeckModal, setShowImportDeckModal] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [importCode, setImportCode] = useState('');
  const [previewDeck, setPreviewDeck] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [carouselCount, setCarouselCount] = useState(() => Number(localStorage.getItem('carouselCount')) || 4);
  const [shuffleInterval, setShuffleInterval] = useState(() => Number(localStorage.getItem('shuffleInterval')) || 5);
  const [isAutoShuffle, setIsAutoShuffle] = useState(true);
  const [shuffledCards, setShuffledCards] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(5);
  const [geminiApiKey, setGeminiApiKey] = useState(() => localStorage.getItem('geminiApiKey') || DEFAULT_GEMINI_KEY);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageData, setPreviewImageData] = useState(null);
  const [showRecentChangesModal, setShowRecentChangesModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [toast, setToast] = useState(null);
  const [dupWarning, setDupWarning] = useState({ title: false, url: false });
  const [addAnyway, setAddAnyway] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [showFeedbackMenu, setShowFeedbackMenu] = useState(false);
  const [showMoreActionsMenu, setShowMoreActionsMenu] = useState(false);
  const [generateCount, setGenerateCount] = useState(0);
  const [visibleCardCount, setVisibleCardCount] = useState(CARDS_INITIAL_PAGE);
  const [holdDeleteId, setHoldDeleteId] = useState(null);
  const [holdDeleteProgress, setHoldDeleteProgress] = useState(0);
  // Collapsible stats section under the Import Deck button row. Stats are a
  // tertiary accessory to search/save — this hides them by default and remembers
  // the user's choice across sessions in localStorage.
  const [statsOpen, setStatsOpen] = useState(() => {
    const saved = localStorage.getItem('richvault_stats_open');
    return saved === 'true';
  });
  useEffect(() => {
    localStorage.setItem('richvault_stats_open', String(statsOpen));
  }, [statsOpen]);
  // Hold-to-delete-account (Settings): 8s hold with left-to-right fill.
  const [holdDeleteAccountProgress, setHoldDeleteAccountProgress] = useState(0);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const titleDupTimer = useRef(null);
  const urlDupTimer = useRef(null);
  const holdDeleteTimer = useRef(null);
  const holdDeleteAccountTimer = useRef(null);
  // Tracks whether onAuthStateChanged has fired at least once — used so we only
  // auto-open the onboarding modal on the initial page-open for signed-out users,
  // not every time they sign out later in the same session.
  const initialAuthResolved = useRef(false);

  const showToast = (message, type = 'warning', duration = 4000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), duration);
  };

  // Save-failure diagnostics don't reach us from the field otherwise — this keeps the
  // last ERROR_LOG_MAX entries so a user who hits a sync error can pull them from
  // Settings and send them over. Logging itself must never throw.
  const logAppError = (context, error, meta = {}) => {
    console.error(`[${context}]`, error);
    try {
      const entry = {
        ts: new Date().toISOString(),
        context,
        code: error?.code || null,
        message: error?.message || String(error),
        meta,
        appVersion: APP_VERSION,
        userAgent: navigator.userAgent
      };
      const existing = JSON.parse(localStorage.getItem(ERROR_LOG_KEY) || '[]');
      localStorage.setItem(ERROR_LOG_KEY, JSON.stringify([entry, ...existing].slice(0, ERROR_LOG_MAX)));
    } catch {
      // no-op — never let logging break the app
    }
  };

  const downloadErrorLog = () => {
    const log = JSON.parse(localStorage.getItem(ERROR_LOG_KEY) || '[]');
    if (log.length === 0) {
      showToast('No errors logged — nothing to download.', 'success');
      return;
    }
    const blob = new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `richvault-error-log-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast(`Downloaded ${log.length} logged error${log.length === 1 ? '' : 's'}.`, 'success');
  };

  const [newCard, setNewCard] = useState({
    title: '', description: '', url: '', dataType: 'html', tags: [], icon: '', status: 'active', imageData: null, deckId: null
  });

  const [personalDecks, setPersonalDecks] = useState(() => {
    const saved = localStorage.getItem('richvault_personal_decks');
    return saved ? JSON.parse(saved) : INITIAL_DECKS;
  });

  const [newPersonalDeck, setNewPersonalDeck] = useState({
    name: '', description: '', color: 'bg-blue-500'
  });

  useEffect(() => {
    localStorage.setItem('theme', theme);
    // Tailwind darkMode:'class' + CSS variable switch live on <html>
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  // Reduced motion disables Discovery auto-advance (Restyle Spec, a11y rule)
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsAutoShuffle(false);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('carouselCount', carouselCount);
    localStorage.setItem('shuffleInterval', shuffleInterval);
  }, [carouselCount, shuffleInterval]);

  // Generate random 8-character code
  const generateShareCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing chars
    let code = 'RV-';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // djb2 hash — converts a URL string into a short Firestore-safe document ID
  const hashUrl = (url) => {
    let hash = 5381;
    for (let i = 0; i < url.length; i++) {
      hash = ((hash << 5) + hash) ^ url.charCodeAt(i);
      hash = hash >>> 0; // keep unsigned 32-bit
    }
    return 'url_' + hash.toString(36);
  };

  // Import shared deck
  const importSharedDeck = useCallback(async (code) => {
    try {
      const cleanCode = code.toUpperCase().trim();
      const deckRef = doc(db, 'sharedDecks', cleanCode);
      const deckDoc = await getDoc(deckRef);

      if (!deckDoc.exists()) {
        alert('Deck not found! Please check the code and try again.');
        return;
      }

      const sharedDeck = deckDoc.data();
      setPreviewDeck(sharedDeck);

      // View counter is non-critical analytics written by the *viewer*. A recipient
      // (non-owner) may be denied write access to the deck doc (Error-DB-003), so isolate
      // this so a denied counter update can never break the preview after a successful read.
      try {
        await setDoc(deckRef, {
          stats: {
            ...sharedDeck.stats,
            views: (sharedDeck.stats.views || 0) + 1,
            lastViewed: new Date().toISOString()
          }
        }, { merge: true });
      } catch (statErr) {
        console.warn('Shared-deck view counter not updated (non-fatal):', statErr?.code || statErr);
      }

    } catch (error) {
      console.error('Error importing deck:', error);
      alert('Failed to load deck. Please try again.');
    }
  }, []);

  // Publish deck to Firestore
  const publishDeck = useCallback(async (deckId) => {
    if (!user) {
      alert('You must be signed in to share decks!');
      return;
    }

    const deck = personalDecks.find(d => d.id === deckId);
    if (!deck) return;

    const deckCards = cards.filter(c => c.deckId === deckId);
    if (deckCards.length === 0) {
      alert('This deck has no cards to share!');
      return;
    }

    const shareCode = generateShareCode();
    const shareUrl = `${window.location.origin}?shared=${shareCode}`;

    try {
      const sharedDeckData = {
        shareCode,              // field name required by Firestore security rules
        code: shareCode,        // kept for any existing reads that use .code
        title: deck.name,
        description: deck.description || '',
        color: deck.color,
        createdBy: user.uid,
        createdByName: user.displayName || 'Anonymous',
        createdByPhoto: user.photoURL || '',
        createdAt: new Date().toISOString(),
        cards: deckCards,
        cardCount: deckCards.length,
        tags: [...new Set(deckCards.flatMap(c => c.tags))],
        isPublic: true,
        stats: {
          views: 0,
          imports: 0,
          lastViewed: new Date().toISOString()
        }
      };

      await setDoc(doc(db, 'sharedDecks', shareCode), sharedDeckData);

      setShareResult({
        code: shareCode,
        url: shareUrl,
        title: deck.name,
        cardCount: deckCards.length
      });

    } catch (error) {
      console.error('Error publishing deck:', error);
      const reason = error?.code === 'permission-denied'
        ? 'Permission denied — check that you are signed in and your Firebase rules allow sharing.'
        : (error?.message || 'Unknown error');
      alert(`Failed to publish deck.\n\n${reason}`);
    }
  }, [user, personalDecks, cards]);

  // Confirm import - add cards to user's vault
  const confirmImportDeck = useCallback(async () => {
    if (!previewDeck) return;

    // Plan cap applies to imports too — block rather than half-import.
    if (cards.length + previewDeck.cards.length > tier.cardLimit) {
      alert(`This deck has ${previewDeck.cards.length} cards, but your ${tier.label} plan only has room for ${cardsRemaining(cards.length, tier)} more (limit ${tier.cardLimit}). Delete some cards and try again.`);
      return;
    }

    try {
      // Create new deck
      const newDeck = {
        id: Date.now().toString(),
        name: `${previewDeck.title} (Imported)`,
        description: previewDeck.description,
        color: previewDeck.color,
        createdTime: new Date().toISOString()
      };

      setPersonalDecks(prev => [...prev, newDeck]);

      // Import cards with new IDs and assigned to new deck
      const importedCards = previewDeck.cards.map(card => ({
        ...card,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        deckId: newDeck.id,
        createdTime: new Date().toISOString()
      }));

      setCards(prev => [...prev, ...importedCards]);

      // Import counter is non-critical analytics written by the recipient, who may be
      // denied write access to the deck doc (Error-DB-003). Isolate it so a denied counter
      // update can never fail the import itself.
      try {
        const deckRef = doc(db, 'sharedDecks', previewDeck.code);
        await setDoc(deckRef, {
          stats: {
            ...previewDeck.stats,
            imports: (previewDeck.stats.imports || 0) + 1
          }
        }, { merge: true });
      } catch (statErr) {
        console.warn('Shared-deck import counter not updated (non-fatal):', statErr?.code || statErr);
      }

      setPreviewDeck(null);
      setShowImportDeckModal(false);
      setImportCode('');
      alert(`Successfully imported ${importedCards.length} cards!`);

    } catch (error) {
      console.error('Error confirming import:', error);
      alert('Failed to import deck. Please try again.');
    }
  }, [previewDeck, cards, tier]);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        // User signed in - load data from Firestore
        setCardsSyncing(true);
        try {
          await loadUserDataFromFirestore(currentUser.uid);
          await loadUserPlan(currentUser.uid);
        } finally {
          setCardsSyncing(false);
        }
      } else {
        setUserPlan(DEFAULT_PLAN);
        // User signed out - load from localStorage
        const saved = localStorage.getItem('richvault_cards');
        setCards(saved ? JSON.parse(saved) : INITIAL_CARDS);
        // Per Notes for improvement.md: show onboarding to ALL unsigned-in
        // users when the app opens. Only fire on the first auth resolution so
        // signing OUT mid-session doesn't re-open the tour on top of the user.
        if (!initialAuthResolved.current) {
          setShowOnboardingModal(true);
        }
      }
      initialAuthResolved.current = true;
    });

    // Check for shared deck in URL
    const urlParams = new URLSearchParams(window.location.search);
    const sharedCode = urlParams.get('shared');
    if (sharedCode) {
      setImportCode(sharedCode);
      setShowImportDeckModal(true);
      importSharedDeck(sharedCode);
    }

    return () => unsubscribe();
    // loadUserDataFromFirestore is intentionally omitted: it's redefined every
    // render and adding it would resubscribe the auth listener on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importSharedDeck]);

  const getAutoIcon = (tags, title) => {
    if (tags && tags.length > 0) {
      for (let tag of tags) {
        if (TAG_ICONS_BY_KEY[tagKey(tag)]) {
          return TAG_ICONS_BY_KEY[tagKey(tag)];
        }
      }
    }
    if (title) {
      return title.charAt(0).toUpperCase();
    }
    return '🔎';
  };

  // Favicon helper — extracts the domain and builds a Google favicon CDN URL.
  // Returns null if the URL is invalid or empty (emoji auto-icon is used as fallback).
  const getFaviconUrl = (url) => {
    if (!url) return null;
    const checked = parsePublicHttpUrl(url);
    if (!checked.ok) return null; // never leak private hostnames to the favicon service
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(new URL(checked.url).hostname)}&sz=64`;
  };

  // Save cards to localStorage (Firestore is handled per-operation in addCard/updateCard/deleteCard)
  useEffect(() => {
    if (cards.length > 0) {
      try {
        localStorage.setItem('richvault_cards', JSON.stringify(cards));
      } catch (e) {
        if (e.name === 'QuotaExceededError') {
          showToast('Local storage is full. Sign in to use cloud sync, or remove some image cards.', 'warning');
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);

  // Autosave the in-progress Add Card form so closing the modal (or losing the tab)
  // doesn't lose typed-in work — restored on next open. Clears itself once every
  // field is empty again, so there's nothing extra for the user to "discard".
  useEffect(() => {
    if (!showAddModal || editingCard) return;
    const hasContent = newCard.title.trim() || newCard.url.trim() || newCard.description.trim() || newCard.tags.length > 0;
    try {
      if (hasContent) {
        localStorage.setItem(DRAFT_CARD_KEY, JSON.stringify(newCard));
      } else {
        localStorage.removeItem(DRAFT_CARD_KEY);
      }
    } catch (e) {
      // Draft saving is a convenience — never interrupt typing over a storage error.
    }
  }, [newCard, showAddModal, editingCard]);

  // Save decks to localStorage and Firestore
  useEffect(() => {
    // Keep localStorage in sync for guest/offline fallback.
    // Firestore writes are handled directly in createPersonalDeck / deletePersonalDeck.
    localStorage.setItem('richvault_personal_decks', JSON.stringify(personalDecks));
  }, [personalDecks]);

  const shuffleCarousel = useCallback(() => {
    const activeCards = cards.filter(c => c.status === 'active');
    const shuffled = [...activeCards].sort(() => Math.random() - 0.5).slice(0, carouselCount);
    setShuffledCards(shuffled);
    setTimeRemaining(shuffleInterval);
  }, [cards, carouselCount, shuffleInterval]);

  useEffect(() => {
    if (isAutoShuffle && shuffledCards.length > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            shuffleCarousel();
            return shuffleInterval;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isAutoShuffle, shuffleInterval, shuffledCards.length, shuffleCarousel]);

  useEffect(() => {
    if (cards.length > 0) shuffleCarousel();
  }, [cards.length, carouselCount, shuffleCarousel]);

  const stats = {
    active: cards.filter(c => c.status === 'active').length,
    draft: cards.filter(c => c.status === 'draft').length,
    archive: cards.filter(c => c.status === 'archive').length,
    uniqueTags: new Set(cards.flatMap(c => c.tags)).size,
    imageCards: cards.filter(c => c.dataType === 'image').length
  };

  const getFilteredCards = () => {
    let filtered = cards;
    
    if (filterStatus !== 'all') filtered = filtered.filter(c => c.status === filterStatus);
    
    if (selectedDeckFilter !== 'all') {
      if (selectedDeckFilter === 'unassigned') {
        filtered = filtered.filter(c => !c.deckId);
      } else {
        filtered = filtered.filter(c => c.deckId === selectedDeckFilter);
      }
    }
    
    if (selectedTagFilter !== 'all') {
      const wanted = tagKey(selectedTagFilter);
      filtered = filtered.filter(c => c.tags.some(t => tagKey(t) === wanted));
    }

    if (excludedTags.length > 0) {
      const hidden = new Set(excludedTags.map(tagKey));
      filtered = filtered.filter(c => !c.tags.some(t => hidden.has(tagKey(t))));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase().trim();

      if (query.includes(',')) {
        const searchTags = query.split(',').map(tag => tag.trim()).filter(tag => tag);
        filtered = filtered.filter(c =>
          c.title.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query) ||
          (c.url || '').toLowerCase().includes(query) ||
          searchTags.some(searchTag =>
            c.tags.some(cardTag => cardTag.toLowerCase().includes(searchTag))
          )
        );
      } else {
        filtered = filtered.filter(c =>
          c.title.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query) ||
          (c.url || '').toLowerCase().includes(query) ||
          c.tags.some(t => t.toLowerCase().includes(query))
        );
      }
    }
    
    if (sortOrder === 'newest') filtered.sort((a, b) => new Date(b.createdTime) - new Date(a.createdTime));
    else if (sortOrder === 'oldest') filtered.sort((a, b) => new Date(a.createdTime) - new Date(b.createdTime));
    else if (sortOrder === 'a-z') filtered.sort((a, b) => a.title.localeCompare(b.title));
    else if (sortOrder === 'z-a') filtered.sort((a, b) => b.title.localeCompare(a.title));
    return filtered;
  };

  const getRecentCards = () => {
    let recent = [...cards].sort((a, b) => new Date(b.createdTime) - new Date(a.createdTime)).slice(0, 2);
    if (recentFilterStatus !== 'all') recent = recent.filter(c => c.status === recentFilterStatus);
    return recent;
  };

  // Compact, readable form of a card's URL: drop the scheme and "www.",
  // drop a lone trailing slash, then ellipsise past CARD_URL_MAX_CHARS.
  const formatCardUrl = (rawUrl) => {
    const trimmed = (rawUrl || '').trim();
    if (!trimmed) return '';

    const cleaned = trimmed
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/$/, '');

    return cleaned.length > CARD_URL_MAX_CHARS
      ? `${cleaned.slice(0, CARD_URL_MAX_CHARS - 1)}…`
      : cleaned;
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    const dateStr = date.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
    
    const timeStr = date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    if (diffDays === 0) return `Today ${timeStr}`;
    if (diffDays === 1) return `Yesterday ${timeStr}`;
    if (diffDays < 7) return `${diffDays} days ago (${dateStr})`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago (${dateStr})`;
    
    return `${dateStr}`;
  };

  // One display spelling per tag across the vault (case variants collapse to it).
  const getCanonicalTagMap = () => buildCanonicalMap(cards, BUILT_IN_TAGS);

  const getAllExistingTags = () => {
    const canonical = getCanonicalTagMap();
    const allTags = cards.flatMap(c => c.tags.map(t => resolveTag(t, canonical)));
    return [...new Set(allTags)].sort();
  };

  const getTagsWithCounts = () => {
    const canonical = getCanonicalTagMap();
    const tagCounts = {};
    cards.forEach(card => {
      dedupeTags(card.tags.map(t => resolveTag(t, canonical))).forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    
    return Object.entries(tagCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([tag, count]) => ({ tag, count }));
  };

  const getDecksWithCounts = () => {
    const deckCounts = {};
    cards.forEach(card => {
      if (card.deckId) {
        deckCounts[card.deckId] = (deckCounts[card.deckId] || 0) + 1;
      }
    });
    
    return personalDecks.map(deck => ({
      ...deck,
      count: deckCounts[deck.id] || 0
    }));
  };

  const createPersonalDeck = async () => {
    const deck = {
      id: Date.now().toString(),
      name: newPersonalDeck.name,
      description: newPersonalDeck.description,
      color: newPersonalDeck.color,
      createdTime: new Date().toISOString(),
      createdBy: user ? user.uid : null
    };
    setPersonalDecks(prev => [...prev, deck]);
    setNewPersonalDeck({ name: '', description: '', color: 'bg-blue-500' });
    setShowPersonalDeckModal(false);

    // Write directly to Firestore — don't rely on the useEffect
    if (user) {
      try {
        const deckRef = doc(db, 'users', user.uid, 'decks', deck.id);
        await setDoc(deckRef, { ...deck, createdBy: user.uid });
      } catch (err) {
        console.error('Error saving deck to Firestore:', err);
        showToast('Deck saved locally but failed to sync to cloud.', 'error');
      }
    }
  };

  const deletePersonalDeck = (deckId) => {
    setCards(prevCards => prevCards.map(card => 
      card.deckId === deckId ? { ...card, deckId: null } : card
    ));
    setPersonalDecks(prevDecks => prevDecks.filter(deck => deck.id !== deckId));
    
    // Delete from Firestore if signed in
    if (user) {
      const deckRef = doc(db, 'users', user.uid, 'decks', deckId);
      deleteDoc(deckRef).catch(err => console.error('Error deleting deck from Firestore:', err));
    }
    
    if (selectedDeckFilter === deckId) {
      setSelectedDeckFilter('all');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Image is too large. Please choose an image under 10MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          let width = img.width;
          let height = img.height;
          const maxSize = 1200;
          
          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          ctx.drawImage(img, 0, 0, width, height);
          
          let quality = 0.8;
          let compressedData = canvas.toDataURL('image/jpeg', quality);
          
          while (compressedData.length > 500 * 1024 && quality > 0.1) {
            quality -= 0.1;
            compressedData = canvas.toDataURL('image/jpeg', quality);
          }
          
          setImagePreview(compressedData);
          setNewCard(prev => ({ ...prev, imageData: compressedData, dataType: 'image' }));
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const generateDescription = async (url) => {
    if (!url) {
      alert('Please enter a URL first!');
      return;
    }

    // SSRF guard: only public http(s) destinations may be sent to the preview service.
    const checked = parsePublicHttpUrl(url);
    if (!checked.ok) {
      alert('That URL cannot be previewed. Use a public http(s) address (no localhost, private network or login-embedded links).');
      return;
    }
    const cleanUrl = checked.url;

    // AD003 waterfall: cache -> site metadata -> (optional) Gemini. The LLM is an
    // enhancement only; if it has no quota (Error-IN-001) we still return a description
    // from Microlink metadata plus heuristic tags instead of failing outright.
    const hasKey = geminiApiKey && geminiApiKey !== 'YOUR_GEMINI_API_KEY_HERE';

    // Heuristic tag picker — matches the app's own tag vocabulary against the site text
    // so tags can still be suggested when the LLM is unavailable.
    const pickHeuristicTags = (text) => {
      const haystack = (text || '').toLowerCase();
      return Object.keys(TAG_COLORS).filter(tag => haystack.includes(tag.toLowerCase())).slice(0, 5);
    };

    setIsGenerating(true);
    try {
      // --- Step A: Check Firestore cache first ---
      const urlHash = hashUrl(cleanUrl);
      const cacheRef = doc(db, 'urlDescriptions', urlHash);
      try {
        const cached = await getDoc(cacheRef);
        if (cached.exists()) {
          const c = cached.data();
          setNewCard(prev => ({ ...prev, description: (c.description || '').slice(0, 250) }));
          setSuggestedTags(c.tags || []);
          showToast('⚡ Description loaded from cache!', 'success');
          return;
        }
      } catch (cacheErr) {
        console.log('Cache read failed, proceeding with API:', cacheErr);
      }

      // Build dynamic tag list from the app's actual TAG_COLORS — always in sync
      const availableTags = Object.keys(TAG_COLORS).join(', ');

      // Build context lines from whatever the user has already typed into the form
      // so Gemini supplements existing work rather than starting from scratch
      const contextParts = [];
      if (newCard.title) contextParts.push(`Card title already entered: "${newCard.title}"`);
      if (newCard.tags.length > 0) contextParts.push(`Tags already assigned (preserve these, only add more if they improve accuracy): ${newCard.tags.join(', ')}`);
      const existingContext = contextParts.length > 0 ? '\n' + contextParts.join('\n') : '';

      // Fetch Microlink metadata (site title, description, publisher)
      let meta = null;
      try {
        const microlinkResponse = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(cleanUrl)}`);
        const microlinkData = await microlinkResponse.json();
        // Re-validate the final destination after any redirects Microlink followed;
        // discard the metadata if it landed somewhere private or non-http(s).
        if (microlinkData.status === 'success' && microlinkData.data &&
            (!microlinkData.data.url || parsePublicHttpUrl(microlinkData.data.url).ok)) {
          meta = microlinkData.data;
        }
      } catch (fetchError) {
        console.log('Could not fetch metadata, using URL only:', fetchError);
      }

      // --- Step C: Smarter prompt ---
      // If Microlink already returned a rich description (>80 chars), use it directly
      // and only ask Gemini to pick tags — saves tokens and conserves daily quota.
      const metaDescription = (meta?.description || '').trim();
      const hasRichMeta = metaDescription.length > 80;
      const heuristicTags = pickHeuristicTags(`${meta?.title || ''} ${metaDescription} ${cleanUrl}`);

      const prompt = hasRichMeta
        ? `You are a digital content curator. A website description is already available — do NOT rewrite it.
Your only task: pick the most accurate 3-5 tags from this list: ${availableTags}. You may also create new ones if none fit.

Website: ${meta.title || cleanUrl}
Description: ${meta.description}${existingContext}

Return only valid JSON with no extra text: {"tags": ["tag1", "tag2", "tag3"]}`
        : meta
        ? `You are a digital content curator. Based on the following website metadata, describe what value this site delivers to its users in 1-2 sentences — focus on what someone would actually use it for, not just what it is.

Website metadata:
- Title: ${meta.title || 'N/A'}
- Description: ${meta.description || 'N/A'}
- Publisher: ${meta.publisher || 'N/A'}
- URL: ${cleanUrl}${existingContext}

Then pick the most accurate 3-5 tags from this list: ${availableTags}. You may also create new ones for sorting if none fit well.

Return only valid JSON with no extra text: {"description": "...", "tags": ["tag1", "tag2", "tag3"]}`
        : `You are a digital content curator. Based on this URL, describe what value this site delivers to its users in 1-2 sentences — focus on what someone would actually use it for.

URL: ${cleanUrl}${existingContext}

Then pick the most accurate 3-5 tags from this list: ${availableTags}. You may also create new ones for sorting if none fit well.

Return only valid JSON with no extra text: {"description": "...", "tags": ["tag1", "tag2", "tag3"]}`;

      // --- Tier 3: Gemini LLM (optional enhancement; failure is non-fatal) ---
      let llmDescription = '';
      let llmTags = null;
      if (hasKey) {
        try {
          // Try models in preference order — fall back on quota / availability errors.
          // Gemini 2.0 models removed — zero free-tier quota as of early 2026.
          const modelsToTry = ['gemini-2.5-flash-lite', 'gemini-2.5-flash'];
          let responseData = null;

          for (let i = 0; i < modelsToTry.length; i++) {
            const model = modelsToTry[i];
            const apiResponse = await fetch(
              `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${geminiApiKey}`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] })
              }
            );

            if (!apiResponse.ok) {
              const errorData = await apiResponse.json();
              const msg = errorData.error?.message || 'API request failed';
              if ((msg.toLowerCase().includes('quota') || msg.includes('QUOTA_EXCEEDED')) && i < modelsToTry.length - 1) {
                console.warn(`${model} unavailable (quota), falling back to ${modelsToTry[i + 1]}`);
                continue;
              }
              throw new Error(msg);
            }

            responseData = await apiResponse.json();
            break; // Success — stop trying
          }

          if (responseData) {
            const text = responseData.candidates[0].content.parts[0].text;
            const jsonMatch = text.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              const result = JSON.parse(jsonMatch[0]);
              llmDescription = result.description || '';
              llmTags = Array.isArray(result.tags) ? result.tags : null;
            }
          }
        } catch (llmErr) {
          // Non-fatal (Error-IN-001): fall through to metadata + heuristic tags.
          console.warn('AI generation unavailable, using metadata only:', llmErr?.message || llmErr);
        }
      }

      // --- Resolve the waterfall: rich meta wins; else LLM description; else any meta ---
      const finalDescription = (hasRichMeta ? metaDescription : (llmDescription || metaDescription)).trim();
      const finalTags = (llmTags && llmTags.length) ? llmTags : heuristicTags;
      const usedLLM = !!(llmDescription || (llmTags && llmTags.length));

      if (!finalDescription) {
        if (!hasKey) {
          alert('No site description was found for this URL. Add a Gemini API key in Settings to generate one, or type a description manually.');
          setShowApiKeyModal(true);
        } else {
          alert('Could not generate a description for this URL. Please try again or type one manually.');
        }
        return;
      }

      // Fill description only — tags are surfaced as clickable suggestions the user picks from.
      setNewCard(prev => ({ ...prev, description: finalDescription.slice(0, 250) }));
      setSuggestedTags(finalTags);

      if (!usedLLM) {
        showToast('Description pulled from site metadata (AI not used).', 'warning');
      }

      // --- Write result to Firestore cache (non-fatal) — so repeat URLs are instant ---
      try {
        await setDoc(cacheRef, {
          url: cleanUrl,
          description: finalDescription,
          tags: finalTags,
          cachedAt: serverTimestamp(),
          cachedBy: user?.uid || 'guest'
        });
      } catch (cacheWriteErr) {
        console.log('Cache write failed (non-fatal):', cacheWriteErr);
      }

      // --- Session counter — only count real LLM calls ---
      if (usedLLM) {
        const newCount = generateCount + 1;
        setGenerateCount(newCount);
        if (newCount >= 3) {
          showToast(
            `⚠️ You've used ${newCount} AI generations this session. Free tier is limited — add your own Gemini API key in Settings for higher limits.`,
            'warning',
            8000
          );
        }
      }
    } catch (error) {
      console.error('Error generating description:', error);
      if (error.message.includes('API_KEY_INVALID')) {
        alert('Invalid API key. Please check your Gemini API key in settings.');
      } else if (error.message.includes('QUOTA_EXCEEDED')) {
        alert('API quota exceeded. Please try again later or use your own API key.');
      } else {
        alert(`Failed to generate description: ${error.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Duplicate detection — uses limit(1) to minimise Firestore reads
  const checkDuplicate = useCallback(async (field, value) => {
    if (!value || editingCard) return; // skip empty values and edit mode
    const normalizedValue =
      field === 'url' && value && !value.match(/^https?:\/\//i)
        ? 'https://' + value
        : value;
    let found = false;
    if (user) {
      try {
        const q = query(
          collection(db, 'users', user.uid, 'cards'),
          where(field, '==', normalizedValue),
          limit(1)
        );
        const snap = await getDocs(q);
        found = !snap.empty;
      } catch (e) {
        console.error('Duplicate check error:', e);
      }
    } else {
      // Guest mode — check in-memory cards array
      found = cards.some(
        c => (c[field] || '').toLowerCase() === normalizedValue.toLowerCase()
      );
    }
    setDupWarning(prev => ({ ...prev, [field]: found }));
  }, [user, cards, editingCard]);

  const withScheme = (url) => (url && !url.match(/^https?:\/\//i) ? 'https://' + url : url);

  // Returns a user-facing message if the card would be rejected by firestore.rules, else null.
  const getCardLimitError = (card) => {
    const title = (card.title || '').trim();
    if (!title) return 'Add a title before saving.';
    if (title.length > CARD_TITLE_MAX) return `Title is ${title.length} characters — the limit is ${CARD_TITLE_MAX}.`;
    const url = withScheme(card.url || '');
    if (url.length > CARD_URL_MAX) return `URL is ${url.length} characters — the limit is ${CARD_URL_MAX}. Try removing tracking parameters (everything after "?").`;
    if ((card.description || '').length > 250) return 'Description is over the 250-character limit.';
    if ((card.tags || []).length > CARD_TAGS_MAX) return `Too many tags (${card.tags.length}) — the limit is ${CARD_TAGS_MAX}.`;
    return null;
  };

  const addCard = () => {
    // Plan cap (mirrored server-side by firestore.rules T-4)
    if (cards.length >= tier.cardLimit) {
      showToast(`Card limit reached (${tier.cardLimit} on the ${tier.label} plan). Delete some cards to add more.`, 'error');
      return;
    }
    // Block save if a duplicate was detected and user hasn't checked "Add Anyway?"
    if ((dupWarning.title || dupWarning.url) && !addAnyway) return;

    const limitError = getCardLimitError(newCard);
    if (limitError) {
      showToast(limitError, 'error');
      return;
    }

    // Favicon first (matches what the icon preview already shows), emoji as fallback
    // when there's no URL or the favicon lookup came back empty.
    const finalIcon = newCard.icon || faviconUrl || getAutoIcon(newCard.tags, newCard.title);

    const finalUrl = withScheme(newCard.url);

    const card = {
      ...newCard,
      icon: finalIcon,
      url: finalUrl,
      id: Date.now().toString(),
      createdTime: new Date().toISOString(), // Local ISO string for UI sorting/display
      createdBy: user ? user.uid : null      // P-2: required for Firestore ownership check
    };
    setCards([...cards, card]);
    createCardInFirestore(card); // New cards use serverTimestamp + createdBy (create rule)
    setShowAddModal(false);
    setNewCard({ title: '', description: '', url: '', dataType: 'html', tags: [], icon: '', status: 'active', imageData: null, deckId: null });
    setImagePreview(null);
    setSuggestedTags([]);
    setTagInput('');
    setDupWarning({ title: false, url: false });
    setAddAnyway(false);
    setFaviconUrl(null);
    localStorage.removeItem(DRAFT_CARD_KEY);
  };

  // Returns false (and keeps the modal open) when the edit would be rejected.
  const updateCard = (id, updates) => {
    const limitError = getCardLimitError(updates);
    if (limitError) {
      showToast(limitError, 'error');
      return false;
    }
    const updatedCard = { ...cards.find(c => c.id === id), ...updates, url: withScheme(updates.url) };
    setCards(cards.map(c => c.id === id ? updatedCard : c));
    saveCardToFirestore(updatedCard);
    return true;
  };

  const deleteCard = (id) => {
    // Firestore delete + counter decrement run once, outside the state updater (an updater
    // may be invoked twice in StrictMode and must stay pure).
    if (user) {
      deleteCardWithCounter(db, user.uid, id)
        .catch(err => logAppError('deleteCardWithCounter', err, { cardId: id }));
    }
    setCards(currentCards => {
      const updatedCards = currentCards.filter(c => c.id !== id);
      localStorage.setItem('richvault_cards', JSON.stringify(updatedCards));
      return updatedCards;
    });
    
    setShuffledCards(currentShuffled => currentShuffled.filter(c => c.id !== id));
  };

  // Press-and-hold delete: the hold itself is the confirmation, so there is no
  // extra dialog. Releasing (or leaving the button) before HOLD_DELETE_MS cancels.
  const cancelHoldDelete = useCallback(() => {
    if (holdDeleteTimer.current) {
      clearInterval(holdDeleteTimer.current);
      holdDeleteTimer.current = null;
    }
    setHoldDeleteId(null);
    setHoldDeleteProgress(0);
  }, []);

  const startHoldDelete = (card, onDeleted) => {
    cancelHoldDelete();
    setHoldDeleteId(card.id);
    setHoldDeleteProgress(0);

    const startedAt = Date.now();
    holdDeleteTimer.current = setInterval(() => {
      const progress = Math.min(100, ((Date.now() - startedAt) / HOLD_DELETE_MS) * 100);
      setHoldDeleteProgress(progress);

      if (progress >= 100) {
        cancelHoldDelete();
        deleteCard(card.id);
        onDeleted?.();
        showToast(`Deleted "${card.title}"`, 'success');
      }
    }, 40);
  };

  // Never leave the hold timer running if the component goes away mid-press.
  useEffect(() => cancelHoldDelete, [cancelHoldDelete]);

  // CSV export — opens in Google Sheets / Excel. Escapes quotes and wraps
  // any field containing a comma, quote, or newline per RFC 4180. Writes a
  // BOM so Excel opens UTF-8 tag names / emoji correctly.
  const exportCardsAsCsv = () => {
    const decksById = Object.fromEntries(personalDecks.map(d => [d.id, d.name]));
    const rows = getFilteredCards();
    if (rows.length === 0) {
      showToast('No cards match the current filter — nothing to export.', 'warning');
      return;
    }
    const columns = ['Title', 'URL', 'Description', 'Tags', 'Deck', 'Status', 'Created'];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [columns.join(',')];
    for (const c of rows) {
      lines.push([
        esc(c.title),
        esc(c.url),
        esc(c.description),
        esc((c.tags || []).join('; ')),
        esc(c.deckId ? (decksById[c.deckId] || '') : ''),
        esc(c.status),
        esc(c.createdTime),
      ].join(','));
    }
    const csv = '﻿' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `richvault-cards-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast(`Exported ${rows.length} card${rows.length === 1 ? '' : 's'} to CSV`, 'success');
  };

  // Hold-to-delete-ACCOUNT (Settings modal). Same 3s hold pattern as cards
  // but wipes: all cards subcollection → all decks subcollection → settings
  // subcollection → Auth user → localStorage. If Firebase requires a fresh
  // sign-in (auth/requires-recent-login) we reauth via popup and retry once.
  const cancelHoldDeleteAccount = useCallback(() => {
    if (holdDeleteAccountTimer.current) {
      clearInterval(holdDeleteAccountTimer.current);
      holdDeleteAccountTimer.current = null;
    }
    setHoldDeleteAccountProgress(0);
  }, []);

  const performAccountDeletion = useCallback(async () => {
    if (!user) return;
    setIsDeletingAccount(true);
    const userId = user.uid;
    try {
      // 1. Wipe user's cards
      // Sequential on purpose: each delete must move the card counter by exactly 1 (rules T-5).
      const cardsSnap = await getDocs(collection(db, 'users', userId, 'cards'));
      await wipeCardsAndCounter(db, userId, cardsSnap.docs.map(d => d.id));
      // 2. Wipe user's decks
      const decksSnap = await getDocs(collection(db, 'users', userId, 'decks'));
      await Promise.all(decksSnap.docs.map(d => deleteDoc(d.ref)));
      // 3. Wipe settings doc
      await deleteDoc(doc(db, 'users', userId, 'settings', 'preferences')).catch(() => {});

      // 4. Delete the Auth user itself. May throw requires-recent-login.
      try {
        await deleteUser(user);
      } catch (authErr) {
        if (authErr && authErr.code === 'auth/requires-recent-login') {
          await reauthenticateWithPopup(user, googleProvider);
          await deleteUser(user);
        } else {
          throw authErr;
        }
      }

      // 5. Local cleanup — remove synced state and reset to initial cards.
      localStorage.removeItem('richvault_cards');
      localStorage.removeItem('richvault_personal_decks');
      localStorage.setItem('hasSeenOnboarding', 'false');
      setCards(INITIAL_CARDS);
      setPersonalDecks(INITIAL_DECKS);
      setUser(null);
      setShowApiKeyModal(false);
      showToast('Account and all data deleted.', 'success');
    } catch (err) {
      console.error('Account deletion failed:', err);
      const reason = err?.code || err?.message || 'unknown';
      showToast(`Failed to delete account (${reason}). Try again.`, 'error');
    } finally {
      setIsDeletingAccount(false);
    }
  }, [user]);

  const startHoldDeleteAccount = () => {
    if (!user || isDeletingAccount) return;
    cancelHoldDeleteAccount();
    setHoldDeleteAccountProgress(0);
    const startedAt = Date.now();
    holdDeleteAccountTimer.current = setInterval(() => {
      const progress = Math.min(100, ((Date.now() - startedAt) / HOLD_DELETE_ACCOUNT_MS) * 100);
      setHoldDeleteAccountProgress(progress);
      if (progress >= 100) {
        cancelHoldDeleteAccount();
        performAccountDeletion();
      }
    }, 40);
  };

  useEffect(() => cancelHoldDeleteAccount, [cancelHoldDeleteAccount]);

  // Any change to what's being shown starts the grid back at the first page.
  useEffect(() => {
    setVisibleCardCount(CARDS_INITIAL_PAGE);
  }, [searchQuery, filterStatus, selectedTagFilter, excludedTags, selectedDeckFilter, sortOrder]);

  // Adds tags to the card being edited/created. Typed spellings are matched to the
  // vault's existing tag case-insensitively, so "Github" joins "github" instead of
  // creating a second tag.
  const addTagsToCard = (rawTags) => {
    const canonical = getCanonicalTagMap();
    const target = editingCard || newCard;
    const tags = dedupeTags([...target.tags, ...rawTags.map(t => resolveTag(t, canonical))]);
    if (editingCard) setEditingCard({ ...editingCard, tags });
    else setNewCard({ ...newCard, tags });
  };

  const addTagToCard = (tag) => addTagsToCard([tag]);

  const completeOnboarding = () => {
    setShowOnboardingModal(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
    
    if (user) {
      // Save to Firestore if signed in
      const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
      setDoc(settingsRef, { hasSeenOnboarding: true }, { merge: true });
    }
  };

  const resetOnboarding = () => {
    setShowOnboardingModal(true);
    setShowApiKeyModal(false);
    localStorage.setItem('hasSeenOnboarding', 'false');
    
    if (user) {
      // Update Firestore if signed in
      const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
      setDoc(settingsRef, { hasSeenOnboarding: false }, { merge: true });
    }
  };

  // Firebase Auth Functions
  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setUser(result.user);
      // Load user data from Firestore
      await loadUserDataFromFirestore(result.user.uid);
    } catch (error) {
      console.error('Sign-in error:', error);
      alert('Failed to sign in. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setUser(null);
      // Clear current data and load from localStorage
      const saved = localStorage.getItem('richvault_cards');
      setCards(saved ? JSON.parse(saved) : INITIAL_CARDS);
    } catch (error) {
      console.error('Sign-out error:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  // Firestore Data Functions
  //
  // FIRESTORE_SYNC_STALE_MS: skip the full cards/decks/settings re-fetch when this
  // user was synced within the window (AD006). Every write already updates `cards`/
  // `personalDecks` state directly and mirrors it to localStorage (see the useEffects
  // above), so a recent local snapshot is trustworthy — it just isn't guaranteed to
  // reflect edits made on a different device in the last few minutes.
  const loadUserDataFromFirestore = async (userId) => {
    try {
      const lastSync = JSON.parse(localStorage.getItem('richvault_last_sync') || 'null');
      if (lastSync && lastSync.uid === userId && (Date.now() - lastSync.ts) < FIRESTORE_SYNC_STALE_MS) {
        const cachedCards = localStorage.getItem('richvault_cards');
        if (cachedCards) {
          setCards(JSON.parse(cachedCards));
          const savedDecks = localStorage.getItem('richvault_personal_decks');
          if (savedDecks) setPersonalDecks(JSON.parse(savedDecks));
          const seen = localStorage.getItem('hasSeenOnboarding');
          if (!seen || seen === 'false') setShowOnboardingModal(true);
          const savedKey = localStorage.getItem('geminiApiKey');
          if (savedKey) setGeminiApiKey(savedKey);
          return; // recently synced — no Firestore reads this login
        }
      }
    } catch {
      // Unreadable cache — fall through to a real fetch below.
    }

    try {
      // Load cards
      // Convert Firestore Timestamps → ISO strings so local sorting and display stay intact.
      // serverTimestamp() writes a Timestamp object; .toDate().toISOString() converts it back.
      const cardsSnapshot = await getDocs(collection(db, 'users', userId, 'cards'));
      const firestoreCards = cardsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdTime: data.createdTime?.toDate?.()?.toISOString() ?? data.createdTime ?? new Date().toISOString()
        };
      });
      
      if (firestoreCards.length > 0) {
        // A card that failed to sync earlier (createCardInFirestore catch) only exists
        // in localStorage. Without this merge, this Firestore-authoritative fetch would
        // silently overwrite and permanently lose it on the next load — Error-BE-001.
        const localSaved = localStorage.getItem('richvault_cards');
        const localCards = localSaved ? JSON.parse(localSaved) : [];
        const firestoreIds = new Set(firestoreCards.map(c => c.id));
        const unsyncedLocalCards = localCards.filter(c => c.createdBy === userId && !firestoreIds.has(c.id));

        setCards([...firestoreCards, ...unsyncedLocalCards]);
        // Best-effort retry, one at a time: parallel transactions on the shared counter doc contend.
        unsyncedLocalCards.reduce((chain, c) => chain.then(() => createCardInFirestore(c)), Promise.resolve());
      } else {
        // No data in Firestore, use localStorage
        const saved = localStorage.getItem('richvault_cards');
        setCards(saved ? JSON.parse(saved) : INITIAL_CARDS);
      }

      // Load decks — isolated try/catch so a permission error here
      // doesn't prevent cards or settings from loading
      try {
        const decksSnapshot = await getDocs(collection(db, 'users', userId, 'decks'));
        const firestoreDecks = decksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (firestoreDecks.length > 0) {
          setPersonalDecks(firestoreDecks);
        } else {
          const savedDecks = localStorage.getItem('richvault_personal_decks');
          if (savedDecks) setPersonalDecks(JSON.parse(savedDecks));
        }
      } catch (deckErr) {
        console.error('Error loading decks from Firestore:', deckErr);
        const savedDecks = localStorage.getItem('richvault_personal_decks');
        if (savedDecks) setPersonalDecks(JSON.parse(savedDecks));
      }

      // Load settings
      const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        const settings = settingsDoc.data();
        if (settings.hasSeenOnboarding !== undefined) {
          localStorage.setItem('hasSeenOnboarding', settings.hasSeenOnboarding.toString());
          if (!settings.hasSeenOnboarding) {
            setShowOnboardingModal(true);
          }
        }
        if (settings.geminiApiKey) {
          setGeminiApiKey(settings.geminiApiKey);
          localStorage.setItem('geminiApiKey', settings.geminiApiKey);
        }
      } else {
        // Check if should show onboarding
        const seen = localStorage.getItem('hasSeenOnboarding');
        if (!seen || seen === 'false') {
          setShowOnboardingModal(true);
        }
      }

      try {
        localStorage.setItem('richvault_last_sync', JSON.stringify({ uid: userId, ts: Date.now() }));
      } catch {
        // Best-effort — a missed timestamp just means the next login re-fetches, which is safe.
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Fallback to localStorage
      const saved = localStorage.getItem('richvault_cards');
      setCards(saved ? JSON.parse(saved) : INITIAL_CARDS);
    }
  };

  // loadUserPlan — reads the read-only entitlement doc. Absent / unreadable / unknown plan
  // all mean the free Low tier; the rules fall back the same way, so the UI never promises
  // more than the server will accept.
  const loadUserPlan = async (userId) => {
    try {
      const snap = await getDoc(doc(db, 'users', userId, 'entitlements', 'tier'));
      const plan = snap.exists() ? snap.data().plan : DEFAULT_PLAN;
      setUserPlan(TIERS[plan] ? plan : DEFAULT_PLAN);
    } catch (error) {
      console.error('Error loading plan, defaulting to Low:', error);
      setUserPlan(DEFAULT_PLAN);
    }
  };

  // createCardInFirestore — used ONLY for new cards.
  // Goes through createCardWithCounter: the card and users/{uid}/meta/counter are written in
  // one transaction, which is what lets firestore.rules enforce the plan cap (T-4).
  // Uses serverTimestamp() so createdTime == request.time passes the P-3 / Category C create rule.
  // createdBy is required by P-2 (isOwner ownership check on update).
  const createCardInFirestore = async (card) => {
    if (!user) return;
    try {
      const firestoreCard = {
        ...card,
        createdBy: user.uid,           // P-2: ownership field — must match auth.uid
        createdTime: serverTimestamp() // Category C: must equal request.time on create
      };
      await createCardWithCounter(db, user.uid, firestoreCard);
    } catch (error) {
      logAppError('createCardInFirestore', error, {
        cardId: card.id,
        title: card.title,
        titleLen: (card.title || '').length,
        urlLen: (card.url || '').length,
        descLen: (card.description || '').length,
        tagCount: (card.tags || []).length,
        status: card.status,
        dataType: card.dataType,
        hasImage: !!card.imageData
      });
      const reason = error?.code || error?.message || 'unknown';
      const capHint = error?.code === 'permission-denied' ? ` This can mean your ${tier.label} plan's ${tier.cardLimit}-card limit is reached.` : '';
      showToast(`Card saved locally but failed to sync. (${reason})${capHint} We'll retry next time you're online.`, 'error');
    }
  };

  // saveCardToFirestore — used ONLY for updates (edits to existing cards).
  // Does NOT overwrite createdTime or createdBy — those are immutable after creation.
  const saveCardToFirestore = async (card) => {
    if (!user) return;
    try {
      const cardRef = doc(db, 'users', user.uid, 'cards', card.id);
      // Immutable after creation (Error-DB-001): createdTime is stored as a Firestore
      // Timestamp but loaded back as an ISO string; re-sending it fails the immutability
      // rule (ISO !== Timestamp) and the write is denied. Strip immutable fields and
      // merge only the mutable ones so the update satisfies the rules.
      const mutable = { ...card };
      delete mutable.createdTime;
      delete mutable.createdBy;
      await setDoc(cardRef, mutable, { merge: true });
    } catch (error) {
      // If the original create never reached Firestore, a merge-set is evaluated as a
      // create and fails because createdTime/createdBy were stripped — recreate instead.
      let docExists = null;
      if (error?.code === 'permission-denied') {
        try {
          docExists = (await getDoc(doc(db, 'users', user.uid, 'cards', card.id))).exists();
        } catch {
          // Leave docExists null — the log entry still records the original failure.
        }
      }
      logAppError('saveCardToFirestore', error, {
        cardId: card.id,
        docExists,
        titleLen: (card.title || '').length,
        urlLen: (card.url || '').length,
        descLen: (card.description || '').length,
        tagCount: (card.tags || []).length,
        status: card.status,
        dataType: card.dataType,
        hasImage: !!card.imageData,
        fields: Object.keys(card).sort()
      });
      if (docExists === false) {
        await createCardInFirestore(card);
        return;
      }
      showToast(`Card saved locally but failed to sync to cloud. (${error?.code || 'unknown'})`, 'error');
    }
  };

  // Self-healing tag merge for existing accounts (v1.3.5): any vault that already holds
  // case variants ("github" + "Github") is collapsed to one spelling the first time it is
  // loaded, and only the cards that actually changed are written back. Idempotent — once
  // merged there is nothing left to change, so it never loops. If a cloud write fails the
  // local copy is still merged and the next login retries.
  useEffect(() => {
    if (showSkeleton) return;
    const { cards: merged, changed } = mergeCardTags(cards, BUILT_IN_TAGS);
    if (changed.length === 0) return;
    setCards(merged);
    if (user) {
      // One at a time — gentle on the write queue, same payload as a normal card edit.
      changed.reduce((chain, c) => chain.then(() => saveCardToFirestore(c)), Promise.resolve());
    }
    showToast(`Combined duplicate tags on ${changed.length} card${changed.length === 1 ? '' : 's'}.`, 'success');
    // saveCardToFirestore / showToast are recreated every render; the merge only needs to
    // re-run when the cards, the load state or the signed-in user change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, showSkeleton, user]);

  const openImagePreview = (imageData) => {
    setPreviewImageData(imageData);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageData(null);
  };

  // Handle ESC key to close image preview
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showImagePreview) {
        closeImagePreview();
      }
    };
    
    if (showImagePreview) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showImagePreview]);

  const handleDescriptionChange = (e, isEditing) => {
    const newDescription = e.target.value;
    
    if (isEditing) {
      setEditingCard({...editingCard, description: newDescription});
    } else {
      setNewCard(prev => ({ ...prev, description: newDescription }));
      
      const wordCount = newDescription.trim().split(/\s+/).filter(word => word.length > 0).length;
      if (wordCount >= 10 && !isGenerating) {
        const lowerDesc = newDescription.toLowerCase();
        const matchedTags = [];
        
        if (lowerDesc.match(/\b(ai|artificial intelligence|machine learning|ml)\b/)) matchedTags.push('AI');
        if (lowerDesc.match(/\b(automat|workflow|script)\b/)) matchedTags.push('automation');
        if (lowerDesc.match(/\b(video|youtube|streaming|watch)\b/)) matchedTags.push('video');
        if (lowerDesc.match(/\b(music|audio|sound|song|spotify)\b/)) matchedTags.push('music');
        if (lowerDesc.match(/\b(social|facebook|twitter|instagram|network)\b/)) matchedTags.push('social media');
        if (lowerDesc.match(/\b(design|graphic|photoshop|figma)\b/)) matchedTags.push('design app');
        if (lowerDesc.match(/\b(image|photo|picture|generate)\b/)) matchedTags.push('image generator');
        if (lowerDesc.match(/\b(note|document|write|editor)\b/)) matchedTags.push('note taking');
        if (lowerDesc.match(/\b(tutorial|learn|guide|course)\b/)) matchedTags.push('tutorial');
        if (lowerDesc.match(/\b(docs|documentation|reference|api)\b/)) matchedTags.push('documentation');
        if (lowerDesc.match(/\b(builder|create|make|tool)\b/)) matchedTags.push('app builder');
        if (lowerDesc.match(/\b(gaming|game|gamer)\b/)) matchedTags.push('gaming');
        if (lowerDesc.match(/\b(education|school|learn)\b/)) matchedTags.push('education');
        if (lowerDesc.match(/\b(recipe|cooking|food)\b/)) matchedTags.push('recipe');
        if (lowerDesc.match(/\b(sales|sell|selling|revenue)\b/)) matchedTags.push('sales');
        if (lowerDesc.match(/\b(business|enterprise|corporate|company)\b/)) matchedTags.push('business');
        if (lowerDesc.match(/\b(ui|interface|component|layout)\b/)) matchedTags.push('ui');
        if (lowerDesc.match(/\b(journal|diary|log|daily)\b/)) matchedTags.push('journal');
        if (lowerDesc.match(/\b(analytics|metrics|data|statistics)\b/)) matchedTags.push('analytics');
        if (lowerDesc.match(/\b(voice|speech|audio ai|tts)\b/)) matchedTags.push('voice ai');
        if (lowerDesc.match(/\b(program|software|application|app)\b/)) matchedTags.push('software');
        if (lowerDesc.match(/\b(security|secure|encrypt|protect)\b/)) matchedTags.push('web security');
        if (lowerDesc.match(/\b(tip|trick|hack|advice)\b/)) matchedTags.push('tips');
        if (lowerDesc.match(/\b(food|restaurant|eat|meal)\b/)) matchedTags.push('food');
        if (lowerDesc.match(/\b(hack|workaround|shortcut)\b/)) matchedTags.push('hacks');
        if (lowerDesc.match(/\b(no code|nocode|low code|visual)\b/)) matchedTags.push('no code');
        if (lowerDesc.match(/\b(generate image|create image|ai art)\b/)) matchedTags.push('image generation');
        if (lowerDesc.match(/\b(image to image|img2img|transform)\b/)) matchedTags.push('image to image');
        if (lowerDesc.match(/\b(text to image|txt2img|prompt to image)\b/)) matchedTags.push('text to image');
        if (lowerDesc.match(/\b(storage|cloud storage|file storage)\b/)) matchedTags.push('web storage');
        if (lowerDesc.match(/\b(learn|study|course|training)\b/)) matchedTags.push('learning');
        if (lowerDesc.match(/\b(database|db|sql|nosql|mongodb)\b/)) matchedTags.push('database');
        
        if (matchedTags.length > 0) {
          setSuggestedTags(matchedTags.slice(0, 5));
        }
      }
    }
  };

  // Token surfaces — light/dark handled by CSS variables (index.css), not ternaries.
  // Borders over shadows: default separation is a 1px --line border.
  const bgClass = 'bg-canvas text-ink';
  const cardBg = 'bg-surface border border-line';
  const inputBg = 'bg-inset border border-line';

  const filteredCards = getFilteredCards();
  const visibleCards = filteredCards.slice(0, visibleCardCount);
  const hiddenCardCount = filteredCards.length - visibleCards.length;

  // Live match of the tag being typed (the segment after the last comma) against
  // the user's own tags, so existing vocabulary wins over near-duplicates.
  const activeTagFragment = tagInput.split(',').pop().trim().toLowerCase();
  const currentCardTags = editingCard ? editingCard.tags : newCard.tags;
  const tagAutocompleteMatches = activeTagFragment
    ? getTagsWithCounts()
        .filter(({ tag }) =>
          tag.toLowerCase().includes(activeTagFragment) &&
          tag.toLowerCase() !== activeTagFragment &&
          !currentCardTags.some(t => tagKey(t) === tagKey(tag)))
        .sort((a, b) => b.count - a.count)
        .slice(0, TAG_AUTOCOMPLETE_LIMIT)
    : [];

  return (
    <div className={`min-h-screen ${bgClass} transition-colors duration-200`}>
      {/* Onboarding Modal */}
      <OnboardingModal 
        isOpen={showOnboardingModal}
        onComplete={completeOnboarding}
        onSkip={completeOnboarding}
        theme={theme}
      />

      <header className="bg-surface border-b border-line p-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">

          {/* ── LEFT: logo + title ── */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Logo mark — direction 1c "Ink tile, emerald V" (RichVault_design/02_Final/RichVault_Wordmark_Icon.html) */}
            <div className="w-8 h-8 rounded-chip bg-[#1A1A18] dark:bg-inset dark:border dark:border-line flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold tracking-tight">
                <span className="text-[#F2F2EF]">R</span><span className="text-[#4FBF9A]">V</span>
              </span>
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold leading-tight tracking-tight">
                <span className="sm:hidden">Rich<span className="text-atext">Vault</span></span>
                <span className="hidden sm:inline">Rich<span className="text-atext">Vault</span> Smart Tags</span>
              </h1>
              <span className="font-mono text-[11px] text-ink3">v{APP_VERSION}</span>
            </div>
          </div>

          {/* ── RIGHT: all icons ── */}
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {loading ? (
              <div className="text-sm opacity-50">Loading...</div>
            ) : user ? (
              <>
                {/* Name + email — desktop only */}
                <div className="text-right hidden sm:block mr-1">
                  <div className="text-sm font-semibold">{user.displayName}</div>
                  <div className="text-xs opacity-70">{user.email}</div>
                </div>
                {/* Avatar — all sizes */}
                <img
                  src={user.photoURL}
                  alt={user.displayName}
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-line flex-shrink-0"
                />
                {/* Sign out — icon only on mobile, icon+text on desktop */}
                <button
                  onClick={handleSignOut}
                  title="Sign Out"
                  className="p-2 sm:px-3 sm:py-2 bg-inset border border-line hover:bg-inset2 text-ink2 hover:text-ink rounded-btn text-sm font-medium transition-colors flex items-center gap-1.5 flex-shrink-0">
                  <LogOut size={16} />
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </>
            ) : (
              <button
                onClick={handleGoogleSignIn}
                className="px-3 py-2 bg-accent hover:bg-accent2 text-white rounded-btn flex items-center gap-2 font-medium transition-colors text-sm flex-shrink-0">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="hidden sm:inline">Sign in with Google</span>
                <span className="sm:hidden">Sign In</span>
              </button>
            )}

            {/* Always visible */}
            <button onClick={() => setShowApiKeyModal(true)} className="p-2 hover:bg-inset rounded-btn flex-shrink-0" title="Settings">
              <Settings size={20} />
            </button>
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 hover:bg-inset rounded-btn flex-shrink-0" title="Toggle theme">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>

        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Intro block — 1c wordmark + tagline. Sits above the stat grid; not sticky
            (only the header is), so it scrolls away on long lists. */}
        <section className="text-center pt-2 pb-1 sm:pt-4 sm:pb-2 space-y-3">
          <h2 className="font-semibold tracking-tight text-3xl sm:text-4xl leading-none">
            Rich<span className="text-atext">Vault</span>
          </h2>
          <p className="text-sm sm:text-base text-ink2 max-w-2xl mx-auto leading-relaxed">
            Save, organize and quickly find all your favorite websites and apps. Add a card to begin.
          </p>
        </section>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 opacity-50" size={20} />
            <input type="text" placeholder="Search cards, tags, descriptions, URLs... (use commas for multiple tags: AI, video, daily use)" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full ${inputBg} rounded-btn pl-10 pr-4 py-3 placeholder:text-ink3`} />
          </div>
          
          {(() => {
            // Filters differing from their defaults — shown as a badge so a collapsed
            // panel never hides the fact that the list is being filtered.
            const activeFilterCount = [
              filterStatus !== 'all',
              selectedTagFilter !== 'all',
              excludedTags.length > 0,
              showDeckView,
              sortOrder !== 'newest'
            ].filter(Boolean).length;
            return (
              <button
                type="button"
                onClick={() => setShowSearchFilters(prev => !prev)}
                aria-expanded={showSearchFilters}
                aria-controls="richvault-search-filters"
                className={`${inputBg} px-3 py-2 rounded-btn text-sm font-medium flex items-center gap-2 text-ink2 hover:border-line2 hover:text-ink transition-colors`}>
                Search filters
                {activeFilterCount > 0 && (
                  <span className="text-xs font-semibold bg-tint text-atext px-2 py-0.5 rounded-chip tabular-nums">
                    {activeFilterCount} active
                  </span>
                )}
                <span className="text-xs opacity-60">{showSearchFilters ? '▲' : '▾'}</span>
              </button>
            );
          })()}

          {showSearchFilters && (
          <div id="richvault-search-filters" className="flex gap-2 flex-wrap items-center">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={`${inputBg} px-3 py-2 rounded-btn text-sm font-medium`}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archive">Archive</option>
            </select>
            
            <select value={selectedTagFilter} onChange={(e) => {
              const tag = e.target.value;
              setSelectedTagFilter(tag);
              // Including and hiding the same tag would always yield nothing.
              setExcludedTags(prev => prev.filter(t => t !== tag));
            }} className={`${inputBg} px-3 py-2 rounded-btn text-sm font-medium`}>
              <option value="all">All Tags ({cards.length})</option>
              {getTagsWithCounts().map(({ tag, count }) => (
                <option key={tag} value={tag}>
                  {tag} ({count})
                </option>
              ))}
            </select>

            <select
              value=""
              onChange={(e) => {
                const tag = e.target.value;
                if (tag) setExcludedTags(prev => (prev.includes(tag) ? prev : [...prev, tag]));
              }}
              aria-label="Hide cards with a tag"
              className={`${inputBg} px-3 py-2 rounded-btn text-sm font-medium`}>
              <option value="">⊘ Hide tag…</option>
              {getTagsWithCounts()
                .filter(({ tag }) => !excludedTags.includes(tag) && tag !== selectedTagFilter)
                .map(({ tag, count }) => (
                  <option key={tag} value={tag}>
                    {tag} ({count})
                  </option>
                ))}
            </select>

            <button
              onClick={() => setShowDeckView(!showDeckView)}
              className={`px-3 py-2 rounded-btn flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
                showDeckView ? 'bg-tint border border-accent text-atext' : `${inputBg} text-ink2 hover:border-line2 hover:text-ink`
              }`}>
              📁 Decks ({personalDecks.length})
            </button>
            
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className={`${inputBg} px-3 py-2 rounded-btn text-sm font-medium`}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="a-z">A-Z</option>
              <option value="z-a">Z-A</option>
            </select>
            
            {searchQuery.includes(',') && (
              <span className="text-xs font-medium bg-tint text-atext px-2 py-1 rounded-chip whitespace-nowrap">
                🔍 Multi-tag
              </span>
            )}

            {selectedTagFilter !== 'all' && (
              <span className="text-xs font-medium bg-tint text-atext px-2 py-1 rounded-chip whitespace-nowrap">
                📌 {selectedTagFilter}
              </span>
            )}

            {excludedTags.map(tag => (
              <button
                key={tag}
                type="button"
                onClick={() => setExcludedTags(prev => prev.filter(t => t !== tag))}
                title={`Show cards tagged "${tag}" again`}
                aria-label={`Stop hiding tag ${tag}`}
                className="text-xs font-medium bg-inset border border-line text-ink2 hover:text-ink hover:border-line2 px-2 py-1 rounded-chip whitespace-nowrap flex items-center gap-1 transition-colors">
                <span className="opacity-60">NOT</span>
                <span className="line-through decoration-1">{tag}</span>
                <X size={12} />
              </button>
            ))}
          </div>
          )}

          {selectedDeckFilter !== 'all' && (
            <div className="text-sm opacity-70">
              Viewing: <strong>
                {selectedDeckFilter === 'unassigned' 
                  ? `Unassigned Cards (${cards.filter(c => !c.deckId).length})`
                  : `${personalDecks.find(d => d.id === selectedDeckFilter)?.name} (${getDecksWithCounts().find(d => d.id === selectedDeckFilter)?.count || 0})`
                }
              </strong>
            </div>
          )}

          {showDeckView && (
            <div className={`${cardBg} rounded-card p-4`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">📁 Your Decks</h3>
                <div className="flex items-center gap-2">
                  {personalDecks.length > 0 && (
                    <button 
                      onClick={() => setShowDeleteDeckModal(true)}
                      className="px-3 py-1 bg-inset border border-line hover:bg-inset2 text-ink2 hover:text-ink rounded-btn text-sm font-medium transition-colors"
                      title="Manage/Delete decks">
                      Manage Decks
                    </button>
                  )}
                  <button 
                    onClick={() => setShowDeckView(false)}
                    className="p-1 hover:bg-inset rounded-btn text-ink3 hover:text-ink transition-colors"
                    title="Close deck view">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <button
                  onClick={() => setSelectedDeckFilter('all')}
                  className={`p-3 rounded-card border transition-colors ${
                    selectedDeckFilter === 'all'
                      ? 'border-accent bg-tint text-atext'
                      : 'border-line bg-inset hover:border-line2'
                  }`}>
                  <div className="text-2xl mb-1">📚</div>
                  <div className="font-medium text-sm">All Cards</div>
                  <div className="text-xs opacity-70">({cards.length} total)</div>
                </button>

                <button
                  onClick={() => setSelectedDeckFilter('unassigned')}
                  className={`p-3 rounded-card border transition-colors ${
                    selectedDeckFilter === 'unassigned'
                      ? 'border-accent bg-tint text-atext'
                      : 'border-line bg-inset hover:border-line2'
                  }`}>
                  <div className="text-2xl mb-1">📂</div>
                  <div className="font-medium text-sm">Unassigned</div>
                  <div className="text-xs opacity-70">({cards.filter(c => !c.deckId).length} cards)</div>
                </button>

                {getDecksWithCounts().map(deck => (
                  <button
                    key={deck.id}
                    onClick={() => setSelectedDeckFilter(deck.id)}
                    className={`p-3 rounded-card border transition-colors ${
                      selectedDeckFilter === deck.id
                        ? 'border-accent bg-tint text-atext'
                        : 'border-line bg-inset hover:border-line2'
                    }`}>
                    <div className="flex items-center justify-center mb-2">
                      <div className={`w-6 h-6 rounded ${deck.color}`}></div>
                    </div>
                    <div className="font-medium text-sm truncate">{deck.name}</div>
                    <div className="text-xs opacity-70">({deck.count} cards)</div>
                  </button>
                ))}

                <button
                  onClick={() => setShowPersonalDeckModal(true)}
                  className="p-3 rounded-card border border-dashed border-line2 bg-inset hover:border-accent transition-colors">
                  <div className="text-2xl mb-1">➕</div>
                  <div className="font-medium text-sm">Create Deck</div>
                  <div className="text-xs opacity-70">New collection</div>
                </button>
              </div>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                try {
                  const draft = JSON.parse(localStorage.getItem(DRAFT_CARD_KEY) || 'null');
                  if (draft) {
                    setNewCard(draft);
                    showToast('Restored your unsaved draft.', 'success');
                  }
                } catch (e) {
                  localStorage.removeItem(DRAFT_CARD_KEY);
                }
                setShowAddModal(true);
              }}
              className="bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn flex items-center justify-center gap-2 font-medium transition-colors">
              <Plus size={20} /> Add Card
            </button>
            <button onClick={() => setShowPersonalDeckModal(true)} className="bg-inset border border-line hover:bg-inset2 text-ink px-6 py-3 rounded-btn flex items-center justify-center gap-2 font-medium transition-colors">
              📂 Create Deck
            </button>

            {/* Export/Share/Import are secondary actions — tucked behind one menu
                (Hick's Law: fewer top-level choices next to the two primary actions). */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowMoreActionsMenu(prev => !prev)}
                aria-expanded={showMoreActionsMenu}
                className="w-full sm:w-auto text-ink2 hover:text-ink hover:bg-inset px-6 py-3 rounded-btn flex items-center justify-center gap-2 font-medium transition-colors">
                More <span className="text-xs opacity-50">{showMoreActionsMenu ? '▲' : '▾'}</span>
              </button>
              {showMoreActionsMenu && (
                <div className="absolute top-full mt-2 left-0 sm:right-0 sm:left-auto bg-surface border border-line rounded-card shadow-modal py-1 min-w-[180px] z-50">
                  <button
                    onClick={() => { exportCardsAsCsv(); setShowMoreActionsMenu(false); }}
                    title="Download visible cards as CSV (opens in Sheets / Excel)"
                    className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-inset transition-colors text-sm whitespace-nowrap">
                    <Save size={16} /> Export CSV
                  </button>
                  <button
                    onClick={() => { setShowShareDeckModal(true); setShowMoreActionsMenu(false); }}
                    className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-inset transition-colors text-sm whitespace-nowrap">
                    <ExternalLink size={16} /> Share Deck
                  </button>
                  <button
                    onClick={() => { setShowImportDeckModal(true); setShowMoreActionsMenu(false); }}
                    className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-inset transition-colors text-sm whitespace-nowrap">
                    📥 Import Deck
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Section divider + Show/Hide stats toggle. The stat grid renders
              right here beneath the toggle (not at the top of the page). Stats
              are a tertiary accessory; collapsed by default; user's choice
              persists in localStorage (see statsOpen state above). */}
          <div className="border-t border-line pt-4 mt-2">
            <button
              type="button"
              onClick={() => setStatsOpen(o => !o)}
              aria-expanded={statsOpen}
              aria-controls="richvault-stats-panel"
              className={`${inputBg} w-full rounded-btn px-4 py-3 flex items-center justify-between hover:border-line2 transition-colors`}>
              <span className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-atext">{statsOpen ? 'Hide your totals' : 'Show your totals'}</span>
                <span className="text-ink3 tabular-nums hidden sm:inline">
                  · {stats.active} active · {stats.draft} drafts · {cards.length} total
                </span>
                <span className="text-ink3 tabular-nums sm:hidden">
                  · {cards.length} total
                </span>
              </span>
              <span className="text-ink3 text-xs" aria-hidden="true">{statsOpen ? '▴' : '▾'}</span>
            </button>

            {statsOpen && (
              <div
                id="richvault-stats-panel"
                className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 mt-4">
                <div className={`${cardBg} p-3 sm:p-4 rounded-card flex flex-col items-center justify-center text-center`}>
                  <div className="text-2xl sm:text-3xl font-semibold text-ink tabular-nums">{stats.active}</div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-ink3 mt-1 leading-tight">Active Cards</div>
                </div>
                <div className={`${cardBg} p-3 sm:p-4 rounded-card flex flex-col items-center justify-center text-center`}>
                  <div className="text-2xl sm:text-3xl font-semibold text-ink tabular-nums">{stats.draft}</div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-ink3 mt-1 leading-tight">Draft Cards</div>
                </div>
                <div className={`${cardBg} p-3 sm:p-4 rounded-card flex flex-col items-center justify-center text-center`}>
                  <div className="text-2xl sm:text-3xl font-semibold text-ink tabular-nums">{stats.uniqueTags}</div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-ink3 mt-1 leading-tight">Unique Tags</div>
                </div>
                <div className={`${cardBg} p-3 sm:p-4 rounded-card flex flex-col items-center justify-center text-center`}>
                  <div className="text-2xl sm:text-3xl font-semibold text-ink tabular-nums">{stats.imageCards}</div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-ink3 mt-1 leading-tight">Image Cards</div>
                </div>
                <div className={`${cardBg} p-3 sm:p-4 rounded-card flex flex-col items-center justify-center text-center col-span-2 sm:col-span-1`}>
                  <div className="text-2xl sm:text-3xl font-semibold text-ink tabular-nums">{cards.length}</div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-ink3 mt-1 leading-tight">Total Items</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={`${cardBg} p-6 rounded-card`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold">Recently Added</h2>
              <select value={recentFilterStatus} onChange={(e) => setRecentFilterStatus(e.target.value)} className={`${inputBg} px-3 py-1 rounded-btn text-sm font-medium`}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archive">Archive</option>
              </select>
            </div>
            <button
              onClick={() => setIsRecentlyAddedMinimized(!isRecentlyAddedMinimized)}
              className="p-2 hover:bg-inset rounded-btn transition-colors"
              title={isRecentlyAddedMinimized ? "Expand Recently Added" : "Minimize Recently Added"}>
              {isRecentlyAddedMinimized ? <Maximize2 size={20} /> : <Minimize2 size={20} />}
            </button>
          </div>
          {!isRecentlyAddedMinimized && (
          <div className="grid grid-cols-2 gap-3">
            {showSkeleton ? <CardSkeletons count={4} compact /> : getRecentCards().map(card => (
              <div key={card.id} className={`${inputBg} border-l-[3px] border-l-accent p-3 rounded-card hover:border-line2 hover:border-l-accent transition-colors flex flex-col`}>
                {card.dataType === 'image' && card.imageData ? (
                  <img
                    src={card.imageData}
                    alt={card.title}
                    className="w-full h-16 object-cover rounded mb-2 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openImagePreview(card.imageData)}
                  />
                ) : (
                  <div className="text-3xl mb-2">
                    {card.icon?.startsWith('http')
                      ? <img src={card.icon} alt="" className="w-8 h-8 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                      : card.icon}
                  </div>
                )}
                <h3 className="font-semibold text-sm truncate">{card.title}</h3>
                <p className="text-xs opacity-70 truncate mb-2">{card.description}</p>
                <div className="flex items-center justify-between mt-auto">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[card.status] || 'bg-ink3'}`}></span>
                    <span className="text-xs font-medium text-ink2 capitalize">{card.status}</span>
                  </div>
                  <button
                    onClick={() => setEditingCard(card)}
                    title="Edit card"
                    className="p-1.5 hover:bg-inset2 rounded-btn transition-colors text-ink2 hover:text-ink">
                    <Edit2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>

        <div className={`${cardBg} p-6 rounded-card`}>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Shuffle size={24} /> Discovery
            </h2>
            <button
              onClick={() => setIsDiscoveryMinimized(!isDiscoveryMinimized)}
              className="p-2 hover:bg-inset rounded-btn transition-colors"
              title={isDiscoveryMinimized ? "Expand Discovery" : "Minimize Discovery"}>
              {isDiscoveryMinimized ? <Maximize2 size={20} /> : <Minimize2 size={20} />}
            </button>
          </div>
          <p className="text-sm opacity-50 mb-4">Resurface cards you've saved and forgot about</p>
          
          {!isDiscoveryMinimized && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <span className="font-mono text-xs text-ink3">Next shuffle: {timeRemaining}s</span>
                <div className="flex items-center gap-4">
                  <button onClick={() => setIsAutoShuffle(!isAutoShuffle)} className="p-2 hover:bg-inset rounded-btn">
                    {isAutoShuffle ? <Pause size={20} /> : <Play size={20} />}
                  </button>
                  <button onClick={shuffleCarousel} className="p-2 hover:bg-inset rounded-btn">
                    <Shuffle size={20} />
                  </button>
                  <button onClick={() => setShowSettingsModal(true)} className="p-2 hover:bg-inset rounded-btn">
                    <Settings size={20} />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {shuffledCards.map(card => (
                  <div key={card.id} className={`${inputBg} p-4 rounded-card hover:border-line2 transition-colors relative`}>
                    {card.dataType === 'image' && card.imageData ? (
                      <img 
                        src={card.imageData} 
                        alt={card.title} 
                        className="w-full h-24 object-contain rounded-chip mb-2 bg-canvas cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => openImagePreview(card.imageData)}
                      />
                    ) : (
                      <div className="text-2xl md:text-3xl mb-2">
                        {card.icon?.startsWith('http')
                          ? <img src={card.icon} alt="" className="w-9 h-9 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                          : card.icon}
                      </div>
                    )}
                    <h3 className="font-semibold text-sm md:text-base truncate mb-1">{card.title}</h3>
                    <p className="text-xs md:text-sm opacity-70 truncate mb-2">{card.description}</p>
                    
                    <div className="flex flex-wrap gap-1 mb-3">
                      {card.tags.slice(0, 4).map(tag => (
                        <span key={tag} className={`${tagChipClass(tag)} text-xs px-2 py-0.5 rounded-chip`}>
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      {card.url && card.url.trim() !== '' && (
                        <a href={safeHref(card.url)} target="_blank" rel="noopener noreferrer"
                          className="flex-1 bg-accent hover:bg-accent2 text-white text-white px-2 py-1 rounded flex items-center justify-center gap-1 text-xs"
                          onClick={(e) => e.stopPropagation()}>
                          <ExternalLink size={12} /> Open
                        </a>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setEditingCard(card); }}
                        className="bg-inset border border-line hover:bg-inset2 text-ink p-1 rounded-chip transition-colors">
                        <Edit2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {showSkeleton ? <CardSkeletons count={6} /> : visibleCards.map(card => (
            <div key={card.id} className={`${cardBg} p-4 rounded-card hover:border-line2 transition-colors relative`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  {card.dataType === 'image' && card.imageData ? (
                    <img 
                      src={card.imageData} 
                      alt={card.title} 
                      className="w-10 h-10 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity" 
                      onClick={() => openImagePreview(card.imageData)}
                   />
                  ) : (
                     card.icon?.startsWith('http')
                       ? <img src={card.icon} alt="" className="w-7 h-7 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                       : <span className="text-2xl">{card.icon}</span>
                   )}
                  <h3 className="font-medium text-base">{card.title}</h3>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[card.status] || 'bg-ink3'}`}></span>
                  <span className="text-xs font-medium text-ink2 capitalize">{card.status}</span>
                </div>
              </div>
              
              <p className="text-sm text-ink2 mb-3 line-clamp-2">{card.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-3">
                {card.tags.map(tag => (
                  <span key={tag} className={`${tagChipClass(tag)} text-xs px-2 py-1 rounded-chip`}>{tag}</span>
                ))}
              </div>
              
              <div className="absolute bottom-3 left-3 max-w-[55%] text-xs opacity-50">
                {card.url && card.url.trim() !== '' && (
                  <div className="font-mono text-[11px] truncate mb-0.5" title={card.url}>
                    {formatCardUrl(card.url)}
                  </div>
                )}
                <div>Added: {formatDate(card.createdTime)}</div>
              </div>

              <div className="flex gap-2 justify-end">
                {card.url && card.url.trim() !== '' && (
                  <a href={safeHref(card.url)} target="_blank" rel="noopener noreferrer" className="bg-accent hover:bg-accent2 text-white text-white px-3 py-2 rounded flex items-center justify-center gap-2 text-sm">
                    <ExternalLink size={16} /> Open
                  </a>
                )}
                <button onClick={() => setEditingCard(card)} className="p-2 hover:bg-inset rounded-btn">
                  <Edit2 size={16} />
                </button>
                <button
                  onMouseDown={() => startHoldDelete(card)}
                  onMouseUp={cancelHoldDelete}
                  onMouseLeave={cancelHoldDelete}
                  onTouchStart={() => startHoldDelete(card)}
                  onTouchEnd={cancelHoldDelete}
                  onTouchCancel={cancelHoldDelete}
                  onContextMenu={(e) => e.preventDefault()}
                  className="relative overflow-hidden select-none p-2 text-ink2 hover:text-danger hover:bg-inset rounded-btn transition-colors"
                  aria-label={`Hold for 3 seconds to delete ${card.title}`}
                  title="Hold for 3 seconds to delete">
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 bg-danger/25"
                    style={{ width: `${holdDeleteId === card.id ? holdDeleteProgress : 0}%` }}
                  />
                  <Trash2 size={16} className="relative" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {hiddenCardCount > 0 && (
          <div className="mt-6 flex flex-col items-center gap-2">
            <button
              onClick={() => setVisibleCardCount(count => count + CARDS_PAGE_STEP)}
              className="bg-inset border border-line hover:bg-inset2 text-ink px-6 py-2.5 rounded-btn text-sm font-medium transition-colors">
              Show {Math.min(CARDS_PAGE_STEP, hiddenCardCount)} more
            </button>
            <span className="text-xs text-ink3 font-mono">
              {visibleCards.length} of {filteredCards.length} cards
            </span>
          </div>
        )}
      </main>

      {(showAddModal || editingCard) && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className={`${cardBg} rounded-modal shadow-modal p-6 max-w-2xl w-full my-8 max-h-[85vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">{editingCard ? 'Edit Card' : 'Add New Card'}</h2>
              <button onClick={() => {
                setShowAddModal(false);
                setEditingCard(null);
                setImagePreview(null);
                setSuggestedTags([]);
                setTagInput('');
                setNewCard({ title: '', description: '', url: '', dataType: 'html', tags: [], icon: '', status: 'active', imageData: null, deckId: null });
                setDupWarning({ title: false, url: false });
                setAddAnyway(false);
                setFaviconUrl(null);
              }} className="p-2 hover:bg-inset rounded-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Data Type</label>
                <select value={editingCard ? editingCard.dataType : newCard.dataType}
                  onChange={(e) => editingCard ? setEditingCard({...editingCard, dataType: e.target.value}) : setNewCard({...newCard, dataType: e.target.value})}
                  className={`w-full ${inputBg} rounded-btn px-4 py-2`}>
                  <option value="html">Website/App</option>
                  <option value="image">Image</option>
                  <option value="application">Application</option>
                </select>
              </div>

              {(!editingCard && newCard.dataType === 'image') && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Upload Image (auto-compresses to ~500KB)</label>
                  <div className={`${inputBg} rounded p-4 text-center`}>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="image-upload" />
                    <label htmlFor="image-upload" className="cursor-pointer flex flex-col items-center gap-2">
                      {imagePreview ? (
                        <div className="space-y-2">
                          <img src={imagePreview} alt="Preview" className="max-h-64 rounded-chip border border-line" />
                          <p className="text-xs opacity-70">Compressed and ready to save</p>
                        </div>
                      ) : (
                        <>
                          <Upload size={40} className="opacity-50" />
                          <span className="text-sm">Click to upload image</span>
                          <span className="text-xs opacity-60">Max 10MB, auto-compresses to ~500KB</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}

              {(editingCard && editingCard.dataType === 'image' && editingCard.imageData) && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Image Preview</label>
                  <div className={`${inputBg} rounded p-4`}>
                    <img src={editingCard.imageData} alt={editingCard.title} className="max-h-96 mx-auto rounded-chip border border-line" />
                  </div>
                </div>
              )}

              {/* New cards get the favicon-or-emoji icon fully automatically (see addCard) —
                  no UI needed. Kept for editing so a wrong/missing icon can still be overridden. */}
              {editingCard && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Icon (auto-generated, or customize)</label>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const ic = editingCard.icon || getAutoIcon(editingCard.tags, editingCard.title);
                      return ic?.startsWith('http')
                        ? <img src={ic} alt="favicon" className="w-10 h-10 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                        : <span className="text-4xl">{ic}</span>;
                    })()}
                    <input type="text" value={editingCard.icon}
                      onChange={(e) => setEditingCard({...editingCard, icon: e.target.value})}
                      className={`flex-1 ${inputBg} rounded-btn px-4 py-2`}
                      placeholder="Auto-generated based on tags or title" />
                    <button
                      onClick={() => {
                        const autoIcon = getAutoIcon(editingCard.tags, editingCard.title);
                        setEditingCard({...editingCard, icon: autoIcon});
                      }}
                      className="bg-inset border border-line hover:bg-inset2 text-ink px-3 py-2 rounded text-sm whitespace-nowrap">
                      Auto
                    </button>
                  </div>
                  <p className="text-xs opacity-60 mt-1">💡 Click "Auto" to reset to an emoji based on tags, or type your own emoji.</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold mb-2">Title</label>
                <input type="text" value={editingCard ? editingCard.title : newCard.title}
                  onChange={(e) => {
                    const val = e.target.value;
                    editingCard ? setEditingCard({...editingCard, title: val}) : setNewCard({...newCard, title: val});
                    if (!editingCard) {
                      setDupWarning(prev => ({ ...prev, title: false }));
                      clearTimeout(titleDupTimer.current);
                      titleDupTimer.current = setTimeout(() => checkDuplicate('title', val), 600);
                    }
                  }}
                  maxLength={CARD_TITLE_MAX}
                  className={`w-full ${inputBg} rounded-btn px-4 py-2`} placeholder="Card title" enterKeyHint="next" />
                {!editingCard && dupWarning.title && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 bg-warn/10 border border-warn/40 rounded-chip px-3 py-2 text-warn text-sm">
                    <span>⚠️ You already have a card with this Title.</span>
                    <label className="flex items-center gap-1.5 cursor-pointer ml-auto whitespace-nowrap">
                      <input type="checkbox" checked={addAnyway} onChange={e => setAddAnyway(e.target.checked)} className="accent-warn" />
                      <span className="font-semibold">Add Anyway?</span>
                    </label>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">URL {newCard.dataType === 'image' && '(optional)'}</label>
                <input type="text" value={editingCard ? editingCard.url : newCard.url}
                  onChange={(e) => {
                    const val = e.target.value;
                    editingCard ? setEditingCard({...editingCard, url: val}) : setNewCard({...newCard, url: val});
                    if (!editingCard) {
                      // Favicon — immediate (pure string work, no network call here)
                      setFaviconUrl(getFaviconUrl(val));
                      // Duplicate check — debounced
                      setDupWarning(prev => ({ ...prev, url: false }));
                      clearTimeout(urlDupTimer.current);
                      urlDupTimer.current = setTimeout(() => checkDuplicate('url', val), 600);
                    }
                  }}
                  className={`w-full ${inputBg} rounded-btn px-4 py-2`} placeholder="https://example.com" enterKeyHint="next" />
                {(() => {
                  const urlLen = withScheme((editingCard ? editingCard.url : newCard.url) || '').length;
                  return urlLen > CARD_URL_MAX && (
                    <p className="mt-1 text-xs text-danger">
                      URL is {urlLen}/{CARD_URL_MAX} characters — too long to sync. Remove tracking parameters (after "?") to shorten it.
                    </p>
                  );
                })()}
                {!editingCard && faviconUrl && (
                  <div className="mt-1 flex items-center gap-2 text-xs opacity-60">
                    <img
                      src={faviconUrl}
                      alt=""
                      className="w-4 h-4 object-contain rounded"
                      onError={() => setFaviconUrl(null)}
                    />
                    <span>Favicon found — will be used as the card icon</span>
                  </div>
                )}
                {!editingCard && dupWarning.url && (
                  <div className="mt-2 flex flex-wrap items-center gap-3 bg-warn/10 border border-warn/40 rounded-chip px-3 py-2 text-warn text-sm">
                    <span>⚠️ You already have a card with this Website.</span>
                    <label className="flex items-center gap-1.5 cursor-pointer ml-auto whitespace-nowrap">
                      <input type="checkbox" checked={addAnyway} onChange={e => setAddAnyway(e.target.checked)} className="accent-warn" />
                      <span className="font-semibold">Add Anyway?</span>
                    </label>
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Description</label>
                <div className="flex gap-2">
                  <textarea 
                    value={editingCard ? editingCard.description : newCard.description}
                    onChange={(e) => handleDescriptionChange(e, !!editingCard)}
                    className={`flex-1 ${inputBg} rounded-btn px-4 py-2`}
                    rows="3"
                    maxLength={250}
                    placeholder="Brief description (max 250 chars)"
                  />
                  {!editingCard && newCard.dataType !== 'image' && (
                    <button 
                      onClick={() => generateDescription(newCard.url)} 
                      disabled={isGenerating}
                      className={`${isGenerating ? 'bg-inset text-ink3 border border-line cursor-wait' : 'bg-inset border border-line hover:bg-inset2 text-ink'} px-4 py-2 rounded-btn font-medium whitespace-nowrap self-start transition-colors`}>
                      {isGenerating ? 'Generating...' : 'Generate'}
                    </button>
                  )}
                </div>
                {suggestedTags.length > 0 && !editingCard && (
                  <p className="text-xs opacity-60 mt-1">💡 Tags auto-suggested based on description</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Tags</label>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {(editingCard ? editingCard.tags : newCard.tags).map(tag => (
                    <span key={tag} className={`${tagChipClass(tag)} text-xs px-3 py-1 rounded-full flex items-center gap-2`}>
                      {tag}
                      <button 
                        onClick={() => {
                          if (editingCard) {
                            setEditingCard({...editingCard, tags: editingCard.tags.filter(t => t !== tag)});
                          } else {
                            setNewCard({...newCard, tags: newCard.tags.filter(t => t !== tag)});
                          }
                        }}
                        className="hover:text-danger rounded-full px-1">
                        ×
                      </button>
                    </span>
                  ))}
                </div>

                {suggestedTags.length > 0 && !editingCard && (
                  <div className="mb-3">
                    <p className="text-xs opacity-70 mb-2">✨ AI Suggested Tags (click to add):</p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedTags.filter(tag => !newCard.tags.some(t => tagKey(t) === tagKey(tag))).map(tag => (
                        <button
                          key={tag}
                          onClick={() => addTagToCard(tag)}
                          className={`${tagChipClass(tag)} text-xs px-3 py-1 rounded-full hover:ring-1 hover:ring-accent`}>
                          + {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {getAllExistingTags().length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs opacity-70 mb-2">📚 Your Existing Tags (click to add):</p>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {getAllExistingTags()
                        .filter(tag => !(editingCard ? editingCard.tags : newCard.tags).some(t => tagKey(t) === tagKey(tag)))
                        .map(tag => (
                        <button
                          key={tag}
                          onClick={() => addTagToCard(tag)}
                          className={`${tagChipClass(tag)} text-xs px-3 py-1 rounded-full hover:ring-1 hover:ring-accent`}>
                          + {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <input
                  type="text"
                  placeholder="Type new tags separated by commas..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTagsToCard(tagInput.split(',').map(t => t.trim()).filter(t => t));
                      setTagInput('');
                    }
                  }}
                  className={`w-full ${inputBg} rounded-btn px-4 py-2`}
                  enterKeyHint="done"
                />

                {tagAutocompleteMatches.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs opacity-70 mb-2">
                      🔎 You already use these — reuse one instead of creating a near-duplicate:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {tagAutocompleteMatches.map(({ tag, count }) => (
                        <button
                          key={tag}
                          onClick={() => {
                            addTagToCard(tag);
                            // Drop only the fragment being typed, keep earlier comma-separated entries.
                            const parts = tagInput.split(',');
                            parts.pop();
                            setTagInput(parts.length ? `${parts.join(',')},` : '');
                          }}
                          className={`${tagChipClass(tag)} text-xs px-3 py-1 rounded-full hover:ring-1 hover:ring-accent`}>
                          {tag} <span className="font-mono opacity-70">×{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs opacity-60 mt-1">Press Enter or Done to add tags</p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Status</label>
                <select value={editingCard ? editingCard.status : newCard.status}
                  onChange={(e) => editingCard ? setEditingCard({...editingCard, status: e.target.value}) : setNewCard({...newCard, status: e.target.value})}
                  className={`w-full ${inputBg} rounded-btn px-4 py-2`}>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archive">Archive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Assign to Deck (Optional)</label>
                <DeckSelect
                  decks={personalDecks}
                  value={editingCard ? (editingCard.deckId || '') : (newCard.deckId || '')}
                  onChange={(id) => {
                    const deckId = id === '' ? null : id;
                    editingCard ? setEditingCard({...editingCard, deckId}) : setNewCard({...newCard, deckId});
                  }}
                  className={`${inputBg} rounded-btn px-4 py-2`} />
                {personalDecks.length === 0 && (
                  <p className="text-xs opacity-60 mt-1">💡 Create a deck first to organize your cards!</p>
                )}
              </div>
              
              {editingCard && (
                <div className={`${inputBg} rounded-card p-3 border-l-4 border-l-accent`}>
                  <div className="flex items-center gap-2 text-sm opacity-70">
                    <span>📅</span>
                    <span>Created: {formatDate(editingCard.createdTime)}</span>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    if (editingCard) {
                      if (updateCard(editingCard.id, editingCard)) setEditingCard(null);
                    } else {
                      addCard();
                    }
                  }}
                  className="flex-1 bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors flex items-center justify-center gap-2">
                  <Save size={20} /> {editingCard ? 'Update Card' : 'Save Card'}
                </button>
                
                <button 
                  onClick={() => { 
                    setShowAddModal(false); 
                    setEditingCard(null); 
                    setImagePreview(null); 
                    setSuggestedTags([]);
                    setTagInput('');
                    setNewCard({ title: '', description: '', url: '', dataType: 'html', tags: [], icon: '', status: 'active', imageData: null, deckId: null });
                  }}
                  className="bg-inset border border-line hover:bg-inset2 text-ink px-6 py-3 rounded-btn font-medium transition-colors flex items-center justify-center gap-2">
                  <X size={20} /> Cancel
                </button>
              </div>

              {editingCard && (
                <button
                  type="button"
                  onMouseDown={() => startHoldDelete(editingCard, () => setEditingCard(null))}
                  onMouseUp={cancelHoldDelete}
                  onMouseLeave={cancelHoldDelete}
                  onTouchStart={() => startHoldDelete(editingCard, () => setEditingCard(null))}
                  onTouchEnd={cancelHoldDelete}
                  onTouchCancel={cancelHoldDelete}
                  onContextMenu={(e) => e.preventDefault()}
                  className="relative overflow-hidden select-none w-full border border-danger/40 text-danger hover:bg-danger/10 px-6 py-3 rounded-btn font-medium transition-colors flex items-center justify-center gap-2"
                  aria-label={`Hold for 3 seconds to delete ${editingCard.title}`}>
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 bg-danger/25"
                    style={{ width: `${holdDeleteId === editingCard.id ? holdDeleteProgress : 0}%` }}
                  />
                  <Trash2 size={20} className="relative" />
                  <span className="relative">{holdDeleteId === editingCard.id ? 'Keep holding to delete…' : 'Hold to Delete Card'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`${cardBg} rounded-modal shadow-modal p-6 max-w-md w-full`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">Discovery Settings</h2>
              <button onClick={() => setShowSettingsModal(false)} className="p-2 hover:bg-inset rounded-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">Cards to Display: {carouselCount}</label>
                <input type="range" min="2" max="7" value={carouselCount}
                  onChange={(e) => setCarouselCount(Number(e.target.value))} className="w-full" />
              </div>
              
              <div>
                <label className="block text-sm font-semibold mb-2">Shuffle Interval: {shuffleInterval}s</label>
                <input type="range" min="3" max="20" value={shuffleInterval}
                  onChange={(e) => setShuffleInterval(Number(e.target.value))} className="w-full" />
              </div>
              
              <button onClick={() => setShowSettingsModal(false)} className="w-full bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors">
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showApiKeyModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className={`${cardBg} rounded-modal shadow-modal px-6 py-4 max-w-md w-full my-8 max-h-[85vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-2xl font-semibold">Settings</h2>
              <button onClick={() => setShowApiKeyModal(false)} className="p-2 hover:bg-inset rounded-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              {(() => {
                const used = cards.length;
                const remaining = cardsRemaining(used, tier);
                const pct = Math.min(100, (used / tier.cardLimit) * 100);
                const barColor = remaining === 0 ? 'bg-danger' : remaining <= Math.ceil(tier.cardLimit * 0.1) ? 'bg-warn' : 'bg-accent';
                return (
                  <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-accent`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold">Your Plan</span>
                      <span className="bg-tint text-atext px-3 py-1 rounded-full text-sm font-medium" data-testid="plan-badge">
                        {tier.label}{tier.note && ` · ${tier.note}`}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-sm font-semibold">Card Saves Remaining</span>
                      <span className="text-2xl font-semibold tabular-nums">{remaining}</span>
                    </div>
                    <div
                      className="h-2 rounded-full bg-inset2 overflow-hidden"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={tier.cardLimit}
                      aria-valuenow={used}
                      aria-label="Card usage">
                      <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs opacity-70 mt-2 tabular-nums">
                      {used} of {tier.cardLimit} cards used
                      {remaining === 0 && ' — delete cards to free up space'}
                    </p>
                    <p className="text-xs opacity-70 mt-2">
                      {tier.key === 'founder'
                        ? 'Thank you for being a Founder — you have the largest vault.'
                        : `Every account is on the free Low plan for now. Plans: ${[TIERS.low, TIERS.med, TIERS.high].map(t => `${t.label} ${t.cardLimit}`).join(' · ')} cards.`}
                    </p>
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-semibold mb-2">Gemini API Key (Optional)</label>
                <input type="password" value={geminiApiKey} onChange={(e) => setGeminiApiKey(e.target.value)}
                  className={`w-full ${inputBg} rounded-btn px-4 py-2`} placeholder="Using default key (or enter your own)" />
                <p className="text-xs opacity-70 mt-2">
                  ✅ Default API key provided for free use<br/>
                  💡 Want your own quota? Get a key at <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-atext underline">Google AI Studio</a>
                </p>
              </div>

              <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-accent`}>
                <button 
                  onClick={resetOnboarding}
                  className="w-full text-left hover:opacity-80 transition-opacity">
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    <span>📚</span>
                    <span>Show Onboarding Tour Again</span>
                  </div>
                  <p className="text-xs opacity-70">Replay the getting started guide</p>
                </button>
              </div>

              <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-accent`}>
                <a 
                  href="https://www.paypal.com/ncp/payment/JGTKNZXEHY5AU"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-left hover:opacity-80 transition-opacity block">
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    <span>☕</span>
                    <span>Buy Me a Coffee</span>
                  </div>
                  <p className="text-xs opacity-70">Support RichVault development</p>
                </a>
              </div>

              <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-accent`}>
                {/* Parent label */}
                <p className="flex items-center gap-2 font-semibold text-base mb-3">
                  <span>💡</span>
                  <span>Get Involved</span>
                </p>
                {/* Sub-items — indented + divided to show hierarchy */}
                <div className="flex flex-col divide-y divide-line ml-2 border-l-2 border-l-accent/30 pl-3">
                  <a
                    href="https://forms.gle/PDGMsJCL2S56anvL6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2 hover:opacity-80 transition-opacity block">
                    <div className="flex items-center gap-2 text-sm font-semibold mb-0.5">
                      <span>💡</span>
                      <span>Share Your Ideas</span>
                    </div>
                    <p className="text-xs opacity-60 pl-6">Send feedback or feature requests</p>
                  </a>
                  <a
                    href="https://forms.gle/2a3kKAQ8gPUrnDiCA"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-2 hover:opacity-80 transition-opacity block">
                    <div className="flex items-center gap-2 text-sm font-semibold mb-0.5">
                      <span>⭐</span>
                      <span>Rate Our App</span>
                    </div>
                    <p className="text-xs opacity-60 pl-6">Leave us a quick rating</p>
                  </a>
                </div>
              </div>
              
              <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-accent`}>
                <p className="flex items-center gap-2 font-semibold text-base mb-1">
                  <span>🛠️</span>
                  <span>Error Log</span>
                </p>
                <p className="text-xs opacity-70 mb-3">
                  Ran into a save error? Download the log and send it to us so we can see what happened.
                </p>
                <button
                  type="button"
                  onClick={downloadErrorLog}
                  className="w-full bg-inset border border-line hover:bg-inset2 text-ink px-4 py-2 rounded-btn text-sm font-medium transition-colors">
                  Download Error Log
                </button>
              </div>

              {user && (
                <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-danger`}>
                  <p className="flex items-center gap-2 font-semibold text-base mb-1">
                    <span>⚠️</span>
                    <span>Delete Account</span>
                  </p>
                  <p className="text-xs opacity-70 mb-3">
                    Permanently removes your account, all saved cards, decks, and settings. Hold the button for 8 seconds to confirm.
                  </p>
                  <button
                    type="button"
                    disabled={isDeletingAccount}
                    onMouseDown={startHoldDeleteAccount}
                    onMouseUp={cancelHoldDeleteAccount}
                    onMouseLeave={cancelHoldDeleteAccount}
                    onTouchStart={startHoldDeleteAccount}
                    onTouchEnd={cancelHoldDeleteAccount}
                    onTouchCancel={cancelHoldDeleteAccount}
                    aria-label="Hold to delete account (8 seconds)"
                    className="relative w-full overflow-hidden rounded-btn font-medium text-white bg-danger hover:bg-danger2 transition-colors select-none disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ userSelect: 'none', WebkitUserSelect: 'none', touchAction: 'none' }}>
                    {/* Left-to-right fill overlay driven by hold progress */}
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 bg-black/30 pointer-events-none transition-none"
                      style={{ width: `${holdDeleteAccountProgress}%` }}
                    />
                    <span className="relative flex items-center justify-center gap-2 px-6 py-3">
                      {isDeletingAccount
                        ? 'Deleting…'
                        : holdDeleteAccountProgress > 0
                          ? `Hold to delete… ${Math.round(holdDeleteAccountProgress)}%`
                          : 'Hold to Delete Account'}
                    </span>
                  </button>
                </div>
              )}

              <button
                onClick={async () => {
                  // 1. Always save locally so it survives a page refresh
                  localStorage.setItem('geminiApiKey', geminiApiKey);

                  // 2. If signed in, also write to Firestore so the key
                  //    follows the user across devices (Android, web, etc.)
                  if (user) {
                    try {
                      const settingsRef = doc(db, 'users', user.uid, 'settings', 'preferences');
                      await setDoc(settingsRef, { geminiApiKey }, { merge: true });
                      showToast('API key saved and synced to your account ✓', 'success');
                    } catch (err) {
                      console.error('Failed to sync API key to Firestore:', err);
                      showToast('Key saved locally but failed to sync to cloud.', 'error');
                    }
                  } else {
                    showToast('API key saved locally. Sign in to sync across devices.', 'warning');
                  }

                  setShowApiKeyModal(false);
                }}
                className="w-full bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors">
                Save API Key
              </button>
            </div>
          </div>
        </div>
      )}

      {showPersonalDeckModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`${cardBg} rounded-modal shadow-modal p-6 max-w-md w-full`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">📁 Create Personal Deck</h2>
              <button onClick={() => setShowPersonalDeckModal(false)} className="p-2 hover:bg-inset rounded-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <input type="text" placeholder="e.g. Learning sites, or Learning for kids" value={newPersonalDeck.name}
                onChange={(e) => setNewPersonalDeck({...newPersonalDeck, name: e.target.value})}
                className={`w-full ${inputBg} rounded-btn px-4 py-2`} />
              
              <div>
                <label className="block text-sm font-semibold mb-2 opacity-70">Pick a colour</label>
                <div className="grid grid-cols-8 gap-2">
                  {[
                    'bg-blue-500', 'bg-indigo-500', 'bg-violet-500', 'bg-purple-500',
                    'bg-pink-500', 'bg-rose-500', 'bg-red-500', 'bg-orange-500',
                    'bg-amber-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500',
                    'bg-emerald-500', 'bg-teal-500', 'bg-cyan-500', 'bg-slate-500'
                  ].map(color => (
                    <button
                      key={color}
                      title={color.replace('bg-', '').replace('-500', '')}
                      onClick={() => setNewPersonalDeck({...newPersonalDeck, color})}
                      className={`w-8 h-8 rounded-full ${color} transition-transform hover:scale-110
                        ${newPersonalDeck.color === color ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface scale-110' : ''}`}
                    />
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button onClick={createPersonalDeck} disabled={!newPersonalDeck.name.trim()}
                  className="flex-1 bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors disabled:opacity-45">
                  Create Deck
                </button>
                <button onClick={() => setShowPersonalDeckModal(false)} className="bg-inset border border-line hover:bg-inset2 text-ink px-6 py-3 rounded-btn font-medium transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteDeckModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`${cardBg} rounded-modal shadow-modal p-6 max-w-md w-full`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">🗑️ Manage Decks</h2>
              <button onClick={() => setShowDeleteDeckModal(false)} className="p-2 hover:bg-inset rounded-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-3">
              {personalDecks.length === 0 ? (
                <p className="text-center opacity-70 py-4">No decks to manage</p>
              ) : (
                personalDecks.map(deck => (
                  <div key={deck.id} className={`${inputBg} rounded-card p-4 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded ${deck.color}`}></div>
                      <div>
                        <div className="font-semibold">{deck.name}</div>
                        <div className="text-xs opacity-70">
                          {getDecksWithCounts().find(d => d.id === deck.id)?.count || 0} cards
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setDeckToDelete(deck)}
                      className="p-2 bg-danger hover:bg-danger2 text-white rounded transition-colors"
                      title="Delete deck">
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <button 
              onClick={() => setShowDeleteDeckModal(false)} 
              className="w-full mt-4 bg-inset border border-line hover:bg-inset2 text-ink px-6 py-3 rounded-btn font-medium transition-colors">
              Close
            </button>
          </div>
        </div>
      )}

      {deckToDelete && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`${cardBg} rounded-modal shadow-modal p-6 max-w-md w-full`}>
            <h2 className="text-2xl font-semibold mb-4">⚠️ Confirm Delete</h2>
            <p className="mb-6">
              Are you sure you want to delete <strong>"{deckToDelete.name}"</strong>?
              <br />
              <span className="text-sm opacity-70">Cards will be unassigned but not deleted.</span>
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  deletePersonalDeck(deckToDelete.id);
                  setDeckToDelete(null);
                  setShowDeleteDeckModal(false);
                }}
                className="flex-1 bg-danger hover:bg-danger2 text-white px-6 py-3 rounded-btn font-medium transition-colors">
                Yes, Delete Deck
              </button>
              <button
                onClick={() => setDeckToDelete(null)}
                className="flex-1 bg-inset border border-line hover:bg-inset2 text-ink px-6 py-3 rounded-btn font-medium transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Recent Changes Modal */}
      {showRecentChangesModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`${cardBg} rounded-modal shadow-modal p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-semibold">🆕 What's New in v{APP_VERSION}</h2>
                <p className="text-sm opacity-70">Recent updates and improvements</p>
              </div>
              <button onClick={() => setShowRecentChangesModal(false)} className="p-2 hover:bg-inset rounded-btn">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Version 1.3.5 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-tint text-atext px-3 py-1 rounded-full text-sm font-medium font-mono">v1.3.5</span>
                  <span className="text-sm opacity-70">Current Version</span>
                </div>
                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>⊘</span> Hide a tag from your search
                    </h3>
                    <p className="text-sm opacity-90">Searching for <em>design</em> but tired of seeing everything tagged <em>github</em>? Open Search filters, choose <strong>Hide tag…</strong>, and those cards drop out of your results. Hidden tags appear as <strong>NOT</strong> chips — click one to bring those cards back. It works together with your search, status, Deck and tag filters.</p>
                  </div>
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>🏷️</span> Tags no longer split by capital letters
                    </h3>
                    <p className="text-sm opacity-90"><em>github</em>, <em>Github</em> and <em>GitHub</em> are now one tag. Tags you already have are combined automatically the next time you open RichVault, and new tags you type match your existing spelling. Filters, hiding and counts treat them as one.</p>
                  </div>
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>📱</span> Deck picker that fits your phone
                    </h3>
                    <p className="text-sm opacity-90">With lots of Decks, the Assign to Deck list used to fill the whole screen on mobile. It now opens as a short scrolling list right under the field, so the rest of the form stays in view.</p>
                  </div>
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>⏳</span> Loading placeholders
                    </h3>
                    <p className="text-sm opacity-90">While your vault loads you'll see softly shimmering placeholder cards instead of an empty page. If your device is set to reduce motion, they stay still.</p>
                  </div>
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>🛡️</span> Safer links and previews
                    </h3>
                    <p className="text-sm opacity-90">Generate only previews public websites, and card links only open normal web addresses. Private-network and unsafe links are blocked.</p>
                  </div>
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>🧭</span> A clearer Getting Started tour
                    </h3>
                    <p className="text-sm opacity-90">New illustrated steps walk through adding a card, generating a description, choosing smart tags, saving it, creating and using a Deck, and finding it later. Examples use real learning websites.</p>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.3.4 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-tint text-atext px-3 py-1 rounded-full text-sm font-medium font-mono">v1.3.4</span>
                  <span className="text-sm opacity-70">Previous Version</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>✨</span> What's new
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Plans</strong> — your card limit now depends on your plan: Low 50 · Medium 110 · High 200 · Founder 350. Everyone starts on the free Low plan.</li>
                      <li>• <strong>Your Plan in Settings</strong> — Settings now shows your plan, how many cards you've used, and how many saves you have left.</li>
                      <li>• <strong>Limits enforced in the cloud</strong> — the card limit is now checked by our database as well as the app, so it can't be bypassed.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.3.3 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.3.3</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>✨</span> What's new
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Simpler action bar</strong> — Add Card and Create Deck take center stage; Export CSV, Share Deck and Import Deck now live under a <em>More</em> menu.</li>
                      <li>• <strong>Stats tucked away</strong> — your totals are hidden by default; tap "Show your totals" under Import Deck whenever you want them.</li>
                      <li>• <strong>Cards remember your work</strong> — start filling out Add Card, close the modal or reload the page, and your draft is still there when you come back.</li>
                      <li>• <strong>Smarter card icons</strong> — new cards automatically use the site's real favicon instead of a generic emoji.</li>
                      <li>• <strong>Downloadable error log</strong> — ran into a save error? Settings → Error Log → Download Error Log gives you something to send us.</li>
                      <li>• <strong>Settings fits small screens</strong> — the Settings popup now scrolls properly instead of getting stuck too tall to close on compact phones.</li>
                    </ul>
                  </div>
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-red-500">🐛</span> Fixed
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• Cards that failed to sync to the cloud were sometimes silently lost on your next visit — they're now kept and retried instead of overwritten.</li>
                      <li>• A new card's icon could occasionally lock onto the wrong emoji depending on typing order — fixed.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.3.2 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.3.2</span>
                  <span className="text-sm opacity-70">Previous Version</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>✨</span> What's new
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Export to CSV</strong> — the Export Cards button now downloads your current filtered view as a spreadsheet-ready file.</li>
                      <li>• <strong>Delete Account, safely</strong> — signed-in users can permanently delete their account and data from Settings with an 8-second hold-to-confirm.</li>
                    </ul>
                  </div>
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-red-500">🐛</span> Fixed
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• The onboarding tour and Share Deck window could get cut off on small phones with no way to reach the buttons — both now scroll properly and stay usable on compact screens.</li>
                      <li>• The onboarding tour now appears every time you open the app signed out, not just the first time.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.3.1 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.3.1</span>
                  <span className="text-sm opacity-70">Earlier Version</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>✨</span> Quality-of-life update
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Search by web address</strong> — the search bar now matches a card's URL as well as its title, description and tags. Type <em>github</em> or <em>figma.com</em> to find everything you saved from there.</li>
                      <li>• <strong>Cards show their site</strong> — each card now displays a short version of its web address just above the added date, so you can tell sources apart at a glance.</li>
                      <li>• <strong>Hold to delete</strong> — the trash button now needs a 3-second press and fills up as you hold it. No more confirmation pop-up, and no more accidental deletions from a stray tap.</li>
                      <li>• <strong>Faster, calmer vault</strong> — big vaults load the first 20 cards, with a <em>Show 10 more</em> button at the bottom and a running count of how many you're viewing.</li>
                      <li>• <strong>Smarter tag typing</strong> — as you type a new tag, RichVault surfaces your existing tags that match (with how often you've used them), so you reuse <em>web dev</em> instead of quietly creating <em>web development</em>.</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.3.0 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.3.0</span>
                  <span className="text-sm opacity-70">Earlier Version</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>🎨</span> "Quiet Archive" Visual Refresh
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>New design language</strong> — Swiss-influenced editorial minimalism with a single Vault-emerald accent on a calm ink-on-paper canvas</li>
                      <li>• <strong>Refined light & dark themes</strong> — warmer neutrals, borders instead of shadows, sharper card corners</li>
                      <li>• <strong>New typography</strong> — Inter for the interface, JetBrains Mono for share codes and metrics</li>
                      <li>• <strong>Quieter tags & statuses</strong> — tinted tag chips and dot-plus-label statuses replace loud colored badges</li>
                      <li>• <strong>Better mobile stats</strong> — the stat grid now centers correctly on small screens</li>
                      <li>• <strong>Accessibility</strong> — consistent focus rings everywhere; reduced-motion turns off Discovery auto-shuffle</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.2.2 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.2.2</span>
                  <span className="text-sm opacity-70">Earlier Version</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-green-500">✨</span> New Features
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>New Onboarding Experience</strong> — 9-slide guided tour with GIF previews for new users</li>
                      <li>• <strong>Starter Cards & Decks</strong> — New users get 17 pre-loaded cards across 4 organised decks (Daily Use, Education, Graphic Design, Useful Sites)</li>
                      <li>• <strong>AI Description Caching</strong> — Repeat URLs load instantly from cache — no API call needed</li>
                      <li>• <strong>Session Usage Counter</strong> — Toast warning after 3 free-tier AI generations so you never hit quota unexpectedly</li>
                      <li>• <strong>Minimize Recently Added</strong> — Collapse the Recently Added section to save screen space</li>
                      <li>• <strong>Firebase Analytics</strong> — Usage insights including device type, session duration, and geographic location</li>
                      <li>• <strong>Card Vault Limit</strong> — Vault capped at 240 cards to keep performance sharp</li>
                    </ul>
                  </div>

                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-blue-500">🔧</span> Improvements
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Smarter AI Prompts</strong> — When a site already has a good description, Generate only picks tags — saving your daily quota</li>
                      <li>• <strong>API Key Syncs Across Devices</strong> — Your Gemini key is now saved to your account, not just this browser</li>
                      <li>• <strong>Deck Sync Fixed</strong> — Decks now correctly save and load across all your signed-in devices</li>
                      <li>• <strong>Mobile Header Redesigned</strong> — Title on the left, all icons on the right — no more overlap on small screens</li>
                      <li>• <strong>Stats Centered on Mobile</strong> — Stat boxes now align correctly on 320 and 375px screens</li>
                      <li>• <strong>Discovery Section Label</strong> — Added description text below the Discovery heading</li>
                      <li>• <strong>Stricter Field Limits</strong> — Description ≤ 250 chars, URL ≤ 250 chars, deck name ≤ 30 chars enforced on save</li>
                    </ul>
                  </div>

                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-red-500">🐛</span> Bug Fixes
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Suggested Tags Clickable Again</strong> — Tags were being auto-added to the card, causing them to filter themselves out of the suggestion list</li>
                      <li>• <strong>Share Deck Fixed</strong> — Published decks were generating an incorrect share code that couldn't be imported</li>
                      <li>• <strong>Favicon Auto-Fetch Fixed</strong> — The Auto button now correctly grabs site favicons</li>
                      <li>• <strong>Gemini Model Updated</strong> — Switched away from the deprecated <code>gemini-1.5-flash</code> to models with active free-tier quota</li>
                      <li>• <strong>Mobile Keyboard Label</strong> — Form inputs now show "Next" on mobile keyboard instead of "Go"</li>
                      <li>• <strong>Edit Button in Recently Added</strong> — Cards in the Recently Added section now have a quick-edit button</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.2.1 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.2.1</span>
                  <span className="text-sm opacity-70">Previous Version</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-green-500">✨</span> New Features
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Toast Notifications</strong> — Non-intrusive alerts for saves, errors, and warnings</li>
                      <li>• <strong>Favicon Previews</strong> — Cards now display the website's favicon instead of a generic emoji</li>
                      <li>• <strong>Duplicate Detection</strong> — Real-time warning when adding a card with a matching title or URL</li>
                      <li>• <strong>Delete Confirmation</strong> — Confirm before permanently removing a card</li>
                      <li>• <strong>Share Ideas / Rate Our App</strong> — Quick feedback links in the footer and settings</li>
                      <li>• <strong>16 Deck Colours</strong> — Expanded colour picker for better deck organisation</li>
                    </ul>
                  </div>

                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-blue-500">🔧</span> Improvements
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Accurate Timestamps</strong> — Cards now use server-side timestamps for correct creation times</li>
                      <li>• <strong>Increased Security</strong> — Stricter Firestore rules for ownership, immutability, and data integrity</li>
                      <li>• Smarter AI description generation with model fallback support</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.1.0 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.1.0</span>
                  <span className="text-sm opacity-70">Previous Version - Major Update</span>
                </div>

                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-green-500">✨</span> New Features
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• <strong>Google Sign-In</strong> - Authenticate with your Google account</li>
                      <li>• <strong>Cloud Sync</strong> - Your data automatically syncs across all devices</li>
                      <li>• <strong>Firestore Integration</strong> - Secure cloud storage for cards and decks</li>
                      <li>• <strong>User Profiles</strong> - See your name and photo in the header</li>
                      <li>• <strong>Guest Mode</strong> - Use without signing in (localStorage only)</li>
                      <li>• Terms of Service with PDF viewer</li>
                      <li>• Recent Changes modal (you're reading it!)</li>
                    </ul>
                  </div>

                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-blue-500">🔧</span> Improvements
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• Multi-device access to your vault when signed in</li>
                      <li>• Automatic sync on all changes</li>
                      <li>• Settings sync across devices (onboarding status, API keys)</li>
                      <li>• Enhanced footer with legal and support links</li>
                    </ul>
                  </div>
                </div>
              </div>

              <hr className="border-line" />

              {/* Version 1.0.1 */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-inset border border-line text-ink2 px-3 py-1 rounded-full text-sm font-medium font-mono">v1.0.1</span>
                  <span className="text-sm opacity-70">Previous Version</span>
                </div>
                
                <div className="space-y-3">
                  <div className={`${inputBg} rounded-card p-4`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span className="text-green-500">✨</span> Features Added
                    </h3>
                    <ul className="text-sm space-y-1 opacity-90 ml-6">
                      <li>• Onboarding tour for new users</li>
                      <li>• Image preview lightbox</li>
                      <li>• Deck management system</li>
                      <li>• 38 total tags with smart suggestions</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Coming Soon Section */}
              <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-warn`}>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <span>🚀</span> Coming Soon
                </h3>
                <ul className="text-sm space-y-1 opacity-90 ml-6">
                  <li>• Shared deck publishing</li>
                  <li>• Advanced export options</li>
                  <li>• Custom tag colors</li>
                  <li>• Keyboard shortcuts</li>
                  <li>• Bulk operations</li>
                </ul>
              </div>
            </div>
            
            <button 
              onClick={() => setShowRecentChangesModal(false)} 
              className="w-full mt-6 bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors">
              Got It!
            </button>
          </div>
        </div>
      )}
      
      {/* Card deletion is confirmed by the 3-second press-and-hold on the card's
          trash button (see startHoldDelete) — no separate confirmation dialog. */}

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`${cardBg} rounded-modal shadow-modal w-full max-w-5xl h-[90vh] flex flex-col`}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-line">
              <div>
                <h2 className="text-2xl font-semibold">Terms of Service</h2>
                <p className="text-sm opacity-70">RichVault Smart Tags</p>
              </div>
              <div className="flex items-center gap-2">
                {!pdfLoadError && (
                  <a
                    href={termsPdf}
                    download="RichVault-Terms-of-Service.pdf"
                    className="px-4 py-2 bg-accent hover:bg-accent2 text-white rounded-btn text-sm font-medium transition-colors flex items-center gap-2"
                    title="Download PDF">
                    <Save size={16} />
                    Download
                  </a>
                )}
                <button
                  onClick={() => { setShowTermsModal(false); setPdfLoadError(false); }}
                  className="p-2 hover:bg-inset rounded-btn">
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* PDF Viewer / Error Fallback */}
            <div className="flex-1 overflow-hidden">
              {pdfLoadError ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
                  <span className="text-5xl">📄</span>
                  <p className="text-lg font-semibold">Could not load the Terms of Service PDF</p>
                  <p className="text-sm opacity-60 max-w-sm">
                    The file may be missing or your browser may not support inline PDF viewing.
                    Try downloading it instead.
                  </p>
                  <a
                    href={termsPdf}
                    download="RichVault-Terms-of-Service.pdf"
                    className="px-6 py-3 bg-accent hover:bg-accent2 text-white rounded-btn font-medium transition-colors flex items-center gap-2">
                    <Save size={16} />
                    Download PDF
                  </a>
                </div>
              ) : (
                <iframe
                  key={termsPdf}
                  src={termsPdf}
                  className="w-full h-full"
                  title="Terms of Service"
                  style={{ border: 'none' }}
                  onError={() => setPdfLoadError(true)}
                />
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-line text-center text-sm opacity-70">
              <p>Last updated: January 2025</p>
            </div>
          </div>
        </div>
      )}

      {/* Share Deck Modal — flex-col + scrollable middle so the close button
          stays pinned and the modal never overflows on tall mobile viewports
          (iPhone 16 Pro Safari). Matches the S022 OnboardingModal pattern. */}
      {showShareDeckModal && (
        <div
          className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-2 sm:p-4 z-50"
          style={{ minHeight: '100vh' }}>
          <div
            className={`${cardBg} rounded-modal shadow-modal max-w-md w-full flex flex-col overflow-hidden`}
            style={{ maxHeight: 'min(90dvh, 100vh - 1rem)' }}>
            {/* Header — pinned, always shows the X close button */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-line flex-shrink-0">
              <h2 className="text-xl sm:text-2xl font-semibold">📤 Share a Deck</h2>
              <button
                onClick={() => {
                  setShowShareDeckModal(false);
                  setShareResult(null);
                }}
                aria-label="Close share deck dialog"
                className="p-2 hover:bg-inset rounded-btn flex-shrink-0">
                <X size={24} />
              </button>
            </div>

            {/* Scrollable body — the ONLY thing that scrolls */}
            <div
              className="flex-1 overflow-y-auto p-4 sm:p-6"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              {!user ? (
                <div className={`${inputBg} rounded-card p-6 text-center`}>
                  <p className="mb-4 opacity-70">You must be signed in to share decks.</p>
                  <button
                    onClick={handleGoogleSignIn}
                    className="px-6 py-3 bg-accent hover:bg-accent2 text-white rounded-btn font-medium transition-colors">
                    Sign In with Google
                  </button>
                </div>
              ) : shareResult ? (
                <div className="space-y-4">
                  <div className={`${inputBg} rounded-card p-4 border-l-4 border-l-accent`}>
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <span>✅</span> Deck Published!
                    </h3>
                    <p className="text-sm opacity-70 mb-2">{shareResult.title}</p>
                    <p className="text-xs opacity-50">{shareResult.cardCount} cards</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Share Code</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={shareResult.code}
                        readOnly
                        className={`flex-1 min-w-0 ${inputBg} rounded-btn px-4 py-2 font-mono text-lg text-center`}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(shareResult.code);
                          alert('Code copied!');
                        }}
                        className="px-4 py-2 bg-accent hover:bg-accent2 text-white rounded-btn flex-shrink-0">
                        Copy
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Share URL</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={shareResult.url}
                        readOnly
                        className={`flex-1 min-w-0 ${inputBg} rounded-btn px-4 py-2 text-sm`}
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(shareResult.url);
                          alert('URL copied!');
                        }}
                        className="px-4 py-2 bg-accent hover:bg-accent2 text-white rounded-btn flex-shrink-0">
                        Copy
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowShareDeckModal(false);
                      setShareResult(null);
                    }}
                    className="w-full bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors">
                    Done
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm opacity-70 mb-4">Select a deck to share with others:</p>
                  {personalDecks.length === 0 ? (
                    <p className="text-center opacity-50 py-4">No decks to share. Create a deck first!</p>
                  ) : (
                    personalDecks.map(deck => {
                      const deckCardCount = cards.filter(c => c.deckId === deck.id).length;
                      return (
                        <button
                          key={deck.id}
                          onClick={() => publishDeck(deck.id)}
                          disabled={deckCardCount === 0}
                          className={`w-full ${inputBg} rounded-card p-4 text-left hover:border-line2 transition-all disabled:opacity-30 disabled:cursor-not-allowed`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded ${deck.color}`}></div>
                              <div>
                                <div className="font-semibold">{deck.name}</div>
                                <div className="text-xs opacity-70">{deckCardCount} cards</div>
                              </div>
                            </div>
                            <ExternalLink size={20} className="opacity-50" />
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Deck Modal */}
      {showImportDeckModal && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center p-4 z-50">
          <div className={`${cardBg} rounded-modal shadow-modal p-6 max-w-md w-full`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold">📥 Import Shared Deck</h2>
              <button onClick={() => {
                setShowImportDeckModal(false);
                setPreviewDeck(null);
                setImportCode('');
              }} className="p-2 hover:bg-inset rounded-btn">
                <X size={24} />
              </button>
            </div>

            {previewDeck ? (
              <div className="space-y-4">
                <div className={`${inputBg} rounded-card p-4`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-8 h-8 rounded ${previewDeck.color}`}></div>
                    <div>
                      <h3 className="font-semibold text-lg">{previewDeck.title}</h3>
                      <p className="text-xs opacity-70">by {previewDeck.createdByName}</p>
                    </div>
                  </div>
                  {previewDeck.description && (
                    <p className="text-sm opacity-80 mb-3">{previewDeck.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm">
                    <span>📊 {previewDeck.cardCount} cards</span>
                    <span>👁️ {previewDeck.stats.views} views</span>
                    <span>📥 {previewDeck.stats.imports} imports</span>
                  </div>
                  {previewDeck.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {previewDeck.tags.slice(0, 5).map(tag => (
                        <span key={tag} className={`${tagChipClass(tag)} text-xs px-2 py-1 rounded-chip`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className={`${inputBg} rounded-card p-3 max-h-48 overflow-y-auto`}>
                  <p className="text-sm font-semibold mb-2">Cards in this deck:</p>
                  {previewDeck.cards.slice(0, 10).map((card, idx) => (
                    <div key={idx} className="text-sm opacity-80 py-1">
                      • {card.title}
                    </div>
                  ))}
                  {previewDeck.cards.length > 10 && (
                    <div className="text-xs opacity-50 mt-1">
                      +{previewDeck.cards.length - 10} more cards...
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={confirmImportDeck}
                    className="flex-1 bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors">
                    Import to My Vault
                  </button>
                  <button
                    onClick={() => {
                      setPreviewDeck(null);
                      setImportCode('');
                    }}
                    className="px-6 py-3 bg-inset border border-line hover:bg-inset2 text-ink rounded-btn font-medium transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm opacity-70">Enter a share code or URL to import a deck:</p>
                
                <div>
                  <label className="block text-sm font-semibold mb-2">Share Code</label>
                  <input
                    type="text"
                    value={importCode}
                    onChange={(e) => setImportCode(e.target.value.toUpperCase())}
                    placeholder="RV-ABC123"
                    className={`w-full ${inputBg} rounded-btn px-4 py-2 font-mono text-center text-lg`}
                  />
                  <p className="text-xs opacity-50 mt-1">Example: RV-ABC123</p>
                </div>

                <button
                  onClick={() => importSharedDeck(importCode)}
                  disabled={!importCode.trim()}
                  className="w-full bg-accent hover:bg-accent2 text-white px-6 py-3 rounded-btn font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  Load Deck Preview
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Image Preview Modal */}
      {showImagePreview && previewImageData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center p-4 z-50"
          onClick={closeImagePreview}>
          <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button 
              onClick={closeImagePreview}
              className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-3 rounded-full transition-colors z-10"
              title="Close (ESC)">
              <X size={24} />
            </button>
            
            <img 
              src={previewImageData} 
              alt="Preview"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded text-sm">
              Click outside or press ESC to close
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] flex items-start gap-3 px-4 py-3 bg-surface border border-line border-l-[3px] rounded-btn shadow-modal text-ink text-sm max-w-sm
          ${toast.type === 'error' ? 'border-l-danger' : toast.type === 'warning' ? 'border-l-warn' : 'border-l-accent'}`}>
          <span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="font-semibold text-ink3 hover:text-ink leading-none">×</button>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-surface border-t border-line mt-12 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm opacity-70">
              <span>🏦</span>
              <span>© 2025 RichVault Smart Tags v{APP_VERSION}</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <button
                onClick={() => setShowRecentChangesModal(true)}
                className="hover:text-atext transition-colors flex items-center gap-1">
                <span>🆕</span>
                <span>What's New</span>
              </button>
              
              <button 
                onClick={() => setShowTermsModal(true)}
                className="hover:text-atext transition-colors">
                Terms of Service
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowFeedbackMenu(prev => !prev)}
                  className="hover:text-atext transition-colors flex items-center gap-1">
                  <span>💡</span>
                  <span>Share Ideas</span>
                  <span className="text-xs opacity-50">{showFeedbackMenu ? '▲' : '▾'}</span>
                </button>
                {showFeedbackMenu && (
                  <div className="absolute bottom-full mb-2 left-0 bg-surface border border-line rounded-card shadow-modal py-1 min-w-[170px] z-50">
                    <a
                      href="https://forms.gle/PDGMsJCL2S56anvL6"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowFeedbackMenu(false)}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-inset transition-colors text-sm whitespace-nowrap">
                      <span>💡</span>
                      <span>Share Your Ideas</span>
                    </a>
                    <a
                      href="https://forms.gle/2a3kKAQ8gPUrnDiCA"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowFeedbackMenu(false)}
                      className="flex items-center gap-2 px-4 py-2 hover:bg-inset transition-colors text-sm whitespace-nowrap">
                      <span>⭐</span>
                      <span>Rate Our App</span>
                    </a>
                  </div>
                )}
              </div>
              
              <a 
                href="https://www.paypal.com/ncp/payment/JGTKNZXEHY5AU"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-atext transition-colors flex items-center gap-1">
                <span>☕</span>
                <span>Support Us</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
